import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Chip, CircularProgress, Paper, Divider, Grid, Tabs, Tab,
  LinearProgress, Tooltip, IconButton, Badge, Collapse, useMediaQuery,
  Menu, MenuItem, ListItemIcon, ListItemText, Autocomplete,
  FormControl, InputLabel, Select, Alert,
} from '@mui/material';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CallIcon from '@mui/icons-material/Call';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import {
  productionOrderService,
  operatorShiftReportService,
  efficiencyLowReasonService,
  orderMaterialDryingService,
  qualityCheckService,
  orderQualityCheckService,
  staffService,
  setupRequestService,
  orderAdditionalProcessService,
  maintenanceTicketService,
  dailyMachineOperatorService,
} from '../services/api';
import api, { authService } from '../services/api';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';

// ─── helpers ─────────────────────────────────────────────────────────────────
/** DRF ValidationError перетворює True → рядок "True". */
function apiFlag(v) {
  return v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true';
}

/** Текст помилки з відповіді API (detail / поля / materials). */
function apiErrorText(data, fallback = 'Помилка') {
  if (data == null || data === '') return fallback;
  if (typeof data === 'string') return data;
  // Пакетний звіт (many=True): помилки приходять масивом — по одному обʼєкту на рядок
  if (Array.isArray(data)) {
    const joined = data.map((x) => apiErrorText(x, '')).filter(Boolean).join(' ');
    return joined || fallback;
  }
  const d = data.detail;
  if (typeof d === 'string' && d.trim()) return d;
  if (Array.isArray(d)) {
    const joined = d.map((x) => (typeof x === 'string' ? x : apiErrorText(x, ''))).filter(Boolean).join(' ');
    if (joined) return joined;
  }
  if (d && typeof d === 'object' && !Array.isArray(d)) {
    const nested = apiErrorText(d, '');
    if (nested) return nested;
  }
  const skip = new Set([
    'detail', 'can_force', 'co_products_warning', 'packaging_warning',
    'unordered_products', 'packaging', 'covered_units', 'materials',
    'blocking_order_id', 'blocking_order_status',
  ]);
  const parts = [];
  if (Array.isArray(data.materials)) {
    parts.push(...data.materials.filter((x) => typeof x === 'string'));
  }
  for (const [k, v] of Object.entries(data)) {
    if (skip.has(k)) continue;
    if (typeof v === 'string' && v.trim()) parts.push(v.trim());
    else if (Array.isArray(v)) {
      for (const item of v) {
        if (typeof item === 'string' && item.trim()) parts.push(item.trim());
      }
    }
  }
  return parts.length ? parts.join(' ') : fallback;
}

function fmtDt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
function fmtTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
}

function useRealtimeCount(order) {
  const [count, setCount] = useState(null);
  const timerRef = useRef(null);
  useEffect(() => {
    if (order.status !== 'in_progress' || !order.launched_at) { setCount(null); return; }
    const cycleSec = order.press_form_production_time_seconds;
    // Гнізда цього виробу (комплект: 2 з 7), не вся форма — інакше ×N завищення + автосписання
    const cavities = order.product_number_of_cavities || order.press_form_number_of_cavities;
    if (!cycleSec || !cavities) { setCount(null); return; }
    const calc = () => Math.floor((Date.now() - new Date(order.launched_at).getTime()) / 1000 / cycleSec) * cavities;
    const tick = () => {
      const units = calc();
      setCount(units);
    };
    tick();
    timerRef.current = setInterval(tick, 30000);
    return () => clearInterval(timerRef.current);
  }, [order.id, order.status, order.launched_at, order.press_form_production_time_seconds, order.product_number_of_cavities, order.press_form_number_of_cavities]); // eslint-disable-line react-hooks/exhaustive-deps
  return count;
}

/** Усі деталі комплекту форми з кількостями (основна + супутні). */
function getOrderKitParts(order) {
  if (!order) return { isKit: false, parts: [] };
  const cos = order.co_produced || [];
  const mainCav = Number(
    order.product_number_of_cavities
    ?? order.manufacturing_config?.number_of_cavities
    ?? order.press_form_number_of_cavities
    ?? 1,
  ) || 1;
  const cycles = Number(order.mold_cycles) > 0
    ? Number(order.mold_cycles)
    : Math.ceil((Number(order.quantity) || 1) / mainCav);
  const parts = [
    {
      key: `main-${order.id}-${order.product}`,
      name: order.product_name || '—',
      qty: Number(order.quantity) || 0,
      produced: Number(order.produced_fact) || 0,
      product_id: Number(order.product) || null,
      co_id: null,
      is_main: true,
      product_weight: Number(order.product_weight) || 0,
    },
    ...cos.map((co) => {
      const lineQty = Number(co.line_quantity);
      const qty = lineQty > 0 ? lineQty : cycles * (Number(co.cavities) || 0);
      return {
        key: `co-${co.id}`,
        name: co.product_name || '—',
        qty,
        produced: Number(co.quantity_produced) || 0,
        product_id: Number(co.product) || null,
        co_id: Number(co.id) || null,
        is_main: false,
        product_weight: Number(co.product_weight) || 0,
      };
    }),
  ];
  return { isKit: cos.length > 0, parts };
}

/** Початкові поля кількостей по деталях комплекту для звіту зміни. */
function buildKitPartQtyState(order, prevLine) {
  const { isKit, parts } = getOrderKitParts(order);
  if (!isKit) return null;
  const prevParts = prevLine?.kit_part_qtys || {};
  const out = {};
  parts.forEach((p) => {
    const prev = prevParts[p.key] || {};
    out[p.key] = {
      product_id: p.product_id,
      co_id: p.co_id,
      is_main: p.is_main,
      name: p.name,
      produced_qty: prev.produced_qty != null ? String(prev.produced_qty) : '',
      defect_qty: prev.defect_qty != null ? String(prev.defect_qty) : '',
    };
  });
  return out;
}

/** Текстовий опис комплекту для звітів: «А × 100, Б × 50». */
function formatOrderKitDescription(order) {
  const { isKit, parts } = getOrderKitParts(order);
  if (!isKit) return order?.product_name || '—';
  return parts.map((p) => `${p.name} × ${Number(p.qty).toLocaleString('uk-UA')}`).join(', ');
}

function KitPartsList({ order, compact = false, showProduced = false, sx = {} }) {
  const { isKit, parts } = getOrderKitParts(order);
  if (!isKit) {
    return (
      <Typography
        variant={compact ? 'subtitle2' : 'h6'}
        sx={{ fontWeight: compact ? 700 : 800, lineHeight: 1.3, ...sx }}
      >
        {order?.product_name || '—'}
      </Typography>
    );
  }
  return (
    <Box sx={{ ...sx }}>
      {parts.map((p) => (
        <Box
          key={p.key}
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 1,
            mb: 0.25,
          }}
        >
          <Typography
            variant={compact ? 'subtitle2' : 'body1'}
            sx={{ fontWeight: 700, lineHeight: 1.3, flex: 1, minWidth: 0 }}
          >
            {p.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontWeight: 800, whiteSpace: 'nowrap', color: 'text.secondary', flexShrink: 0 }}
          >
            {showProduced
              ? `${p.produced.toLocaleString('uk-UA')} / ${p.qty.toLocaleString('uk-UA')} шт`
              : `${p.qty.toLocaleString('uk-UA')} шт`}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function scheduleInfo(order) {
  const produced = Number(order.produced_fact) || 0;
  const total = Number(order.quantity) || 0;
  const remaining = Math.max(0, total - produced);
  const startTime = order.launched_at ? new Date(order.launched_at) : order.actual_start ? new Date(order.actual_start) : order.planned_start ? new Date(order.planned_start) : null;
  const endTime = order.planned_end ? new Date(order.planned_end) : null;
  const now = new Date();
  let onSchedule = null;
  let expectedNow = null;
  let progress = total > 0 ? Math.min(100, Math.round((produced / total) * 100)) : 0;

  if (startTime && endTime && total > 0 && order.status === 'in_progress') {
    const totalMs = endTime - startTime;
    const elapsedMs = now - startTime;
    if (totalMs > 0 && elapsedMs > 0) {
      expectedNow = Math.round((Math.min(elapsedMs, totalMs) / totalMs) * total);
      onSchedule = produced >= expectedNow * 0.95;
    }
  }
  return { produced, total, remaining, onSchedule, expectedNow, progress };
}

function isOrderDryingDone(order) {
  if (!order?.needs_drying) return true;
  if (order.drying_status === 'completed' || order.drying_status === 'not_needed') return true;
  if (order.drying_status === 'in_progress' && order.drying_started_at && order.drying_time_minutes) {
    const finish = new Date(order.drying_started_at).getTime() + Number(order.drying_time_minutes) * 60000;
    return Date.now() >= finish;
  }
  return false;
}

function dryingShouldStartAt(order) {
  if (!order?.needs_drying || !order.drying_time_minutes || !order.planned_start) return null;
  return new Date(new Date(order.planned_start).getTime() - Number(order.drying_time_minutes) * 60000);
}

function getNextAction(order) {
  if (order.status === 'planned') return 'start';
  if (order.status === 'in_progress' && order.setup_at && !order.launched_at) return 'launch';
  if (order.status === 'in_progress' && order.launched_at) return 'complete';
  if (order.status === 'in_progress' && !order.setup_at) {
    const sr = order.setup_request;
    const hasActiveSetup = sr && ['pending', 'in_progress'].includes(sr.status);
    if (order.needs_drying && !isOrderDryingDone(order)) return 'drying';
    if (!hasActiveSetup) return 'request_setup';
    return 'awaiting_setup';
  }
  return null;
}

const ACTION_CFG = {
  start:          { label: 'Почати роботу',   icon: <PlayArrowIcon />,    bg: '#1565c0' },
  drying:         { label: 'Сушіння…',         icon: <WaterDropIcon />,    bg: '#0288d1', disabled: true },
  request_setup:  { label: 'Провести наладку', icon: <BuildIcon />,        bg: '#ef6c00' },
  awaiting_setup: { label: 'Очікує наладку',   icon: <BuildIcon />,        bg: '#757575', disabled: true },
  launch:         { label: 'Запустити',        icon: <RocketLaunchIcon />, bg: '#6a1b9a' },
  complete:       { label: 'Завершити',        icon: <CheckCircleIcon />,  bg: '#2e7d32' },
};

const STATUS_CHIP = {
  planned:     { label: 'Заплановано',     bg: '#e3f2fd', color: '#0d47a1' },
  in_progress: { label: 'В роботі',        bg: '#e8f5e9', color: '#1b5e20' },
  paused:      { label: 'Зупинено',        bg: '#fff3e0', color: '#e65100' },
  completed:   { label: 'Завершено',       bg: '#f3e5f5', color: '#4a148c' },
  cancelled:   { label: 'Скасовано',       bg: '#fbe9e7', color: '#bf360c' },
};

const PAUSE_REASONS = [
  { value: 'no_material', label: 'Нестача сировини' },
  { value: 'no_mix',      label: 'Нестача замісу' },
  { value: 'qc_fail',     label: 'Не пройшло КЯ' },
  { value: 'breakdown',   label: 'Поломка обладнання' },
  { value: 'break',        label: 'Планова перерва' },
  { value: 'maintenance', label: 'Обслуговування' },
];

// ─── Order Status Badge ───────────────────────────────────────────────────────
function OrderChip({ status, size = 'small' }) {
  const cfg = STATUS_CHIP[status] || { label: status, bg: '#eee', color: '#333' };
  return <Chip label={cfg.label} size={size} sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700 }} />;
}

// ─── TPA Tile ─────────────────────────────────────────────────────────────────
function TpaTile({ machine, orders, onClick }) {
  const activeOrders = orders.filter((o) => ['planned', 'in_progress', 'paused'].includes(o.status));
  const current = activeOrders.find((o) => o.status === 'in_progress') || activeOrders[0];
  const queueCount = activeOrders.length;

  return (
    <Paper
      elevation={2}
      onClick={() => onClick(machine, activeOrders)}
      sx={{
        borderRadius: 3, p: 2, mb: 1.5, cursor: 'pointer',
        border: '2px solid transparent',
        transition: 'all 0.15s',
        '&:hover': { borderColor: '#1a237e', transform: 'translateY(-1px)', boxShadow: 4 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PrecisionManufacturingIcon sx={{ color: '#1a237e', fontSize: 20 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1a237e' }}>
            {machine.identifier || machine.name || `ТПА #${machine.id}`}
          </Typography>
        </Box>
        {queueCount > 0 && (
          <Chip label={`${queueCount} в черзі`} size="small" sx={{ bgcolor: '#e8eaf6', color: '#3949ab', fontWeight: 600 }} />
        )}
      </Box>

      {current ? (
        <>
          <Typography variant="body2" sx={{ fontWeight: 800, color: '#283593', display: 'block', mb: 0.25 }}>
            {(current.customer_order_number != null && String(current.customer_order_number).trim() !== '')
              ? `№ ${String(current.customer_order_number).trim()}`
              : 'Без № замовлення'}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, lineHeight: 1.3 }} noWrap>
            {current.product_name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <OrderChip status={current.status} />
            {current.status === 'in_progress' && (() => {
              const { produced, total, onSchedule } = scheduleInfo(current);
              return (
                <Typography variant="caption" color="text.secondary">
                  {produced}/{total} шт
                  {onSchedule !== null && (
                    onSchedule
                      ? <span style={{ color: '#2e7d32', marginLeft: 4 }}>▲ вчасно</span>
                      : <span style={{ color: '#c62828', marginLeft: 4 }}>▼ відстає</span>
                  )}
                </Typography>
              );
            })()}
          </Box>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">Немає активних замовлень</Typography>
      )}
    </Paper>
  );
}

function formatMixRunout(seconds) {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return null;
  if (seconds < 60) return 'менше хвилини';
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} хв`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (m === 0) return `${h} год`;
  return `${h} год ${m} хв`;
}

/** Підпис замісу на картці: «Заміс: 100 кг із 500 кг · закінчиться через 1 год» */
function buildMixCardLine(order, producedLive) {
  const mb = order?.mixing_batch_info;
  if (!mb) return null;

  // кг/шт — з налаштувань виробу (product_weight) / рецепту; цикл і гнізда — з конфігу ТПА
  const kgPerPc = Number(mb.kg_per_pc) || 0;
  const orderQty = Number(order.quantity) || 0;
  const fact = Number(order.produced_fact) || 0;
  // Списання замісу — від реал-тайму (цикл × гнізда з налаштувань), як автосписання матеріалів
  const live = (producedLive != null && Number.isFinite(Number(producedLive)))
    ? Number(producedLive)
    : null;
  const consumedPcs = live != null ? Math.max(fact, live) : fact;
  const remainingPcs = Math.max(0, orderQty - Math.min(consumedPcs, orderQty || consumedPcs));

  let have = Number(mb.available_unused_kg);
  let need = Number(mb.needed_remaining_kg);

  if (kgPerPc > 1e-12) {
    const added = Number(mb.total_added_kg) || 0;
    const required = Number(mb.total_required_kg) || 0;
    // Фактично замішане (з перемісом); якщо DONE без внесень — рецепт
    const mixedKg = added > 1e-12 ? added : (mb.status === 'done' ? required : 0);
    have = Math.max(0, mixedKg - consumedPcs * kgPerPc);
    need = remainingPcs * kgPerPc;
  } else if (!Number.isFinite(have) && !Number.isFinite(need)) {
    const added = Number(mb.total_added_kg) || 0;
    const req = Number(mb.needed_remaining_kg) || Number(mb.total_required_kg) || 0;
    if (added <= 0 && req <= 0) return null;
    return `Заміс: ${added.toLocaleString('uk-UA', { maximumFractionDigits: 1 })} кг із ${req.toLocaleString('uk-UA', { maximumFractionDigits: 1 })} кг`;
  }

  const haveStr = (Number.isFinite(have) ? have : 0).toLocaleString('uk-UA', { maximumFractionDigits: 1 });
  const needStr = (Number.isFinite(need) ? need : 0).toLocaleString('uk-UA', { maximumFractionDigits: 1 });
  let line = `Заміс: ${haveStr} кг із ${needStr} кг`;

  const cavities = Number(mb.cavities) || 0;
  const cycleSec = Number(mb.cycle_seconds) || 0;
  let runoutSec = null;
  if (have > 1e-6 && kgPerPc > 1e-12 && cycleSec > 0 && cavities > 0) {
    const pcsFromMix = have / kgPerPc;
    runoutSec = Math.ceil(pcsFromMix / cavities) * cycleSec;
  } else if (mb.runout_seconds != null) {
    runoutSec = mb.runout_seconds;
  }

  if (runoutSec != null && have > 1e-6) {
    const label = formatMixRunout(runoutSec);
    if (label) line += ` · закінчиться через ${label}`;
  } else if (have <= 1e-6 && need > 1e-6) {
    line += ' · заміс вичерпано';
  }
  return line;
}

// ─── Order Tile ───────────────────────────────────────────────────────────────
function OrderTile({ order, onClick, busy, onAction, onRedoSetup, maintTicket, onMaintStop }) {
  const { produced, total, remaining, onSchedule, progress } = scheduleInfo(order);
  // Автосписання за реал-таймом робить сервер (sync_machine_breaks), сторінка лише показує лічильник
  const realtimeCount = useRealtimeCount(order);
  const action = getNextAction(order);
  const cfg = action ? ACTION_CFG[action] : null;
  const isPausedMaint = order.status === 'paused' && order.pause_reason === 'maintenance';
  const [mixTick, setMixTick] = useState(Date.now());
  useEffect(() => {
    if (!(order.status === 'in_progress' && order.launched_at && order.mixing_batch_info)) return undefined;
    const id = setInterval(() => setMixTick(Date.now()), 10000);
    return () => clearInterval(id);
  }, [order.id, order.status, order.launched_at, order.mixing_batch_info]);
  void mixTick;
  const producedForMix = realtimeCount != null ? realtimeCount : produced;
  const mixLine = buildMixCardLine(order, producedForMix);
  const orderNo = (order.customer_order_number != null && String(order.customer_order_number).trim() !== '')
    ? String(order.customer_order_number).trim()
    : null;

  return (
    <Paper
      elevation={2}
      sx={{
        borderRadius: 3, overflow: 'hidden', mb: 1.5, minHeight: 220,
        border: '2px solid transparent',
        transition: 'all 0.15s',
        '&:hover': { borderColor: '#1a237e', boxShadow: 4 },
      }}
    >
      {/* Clickable header area */}
      <Box
        onClick={() => onClick(order)}
        sx={{ p: 2.5, cursor: 'pointer', '&:hover': { bgcolor: '#f5f7ff' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.25 }}>
          <Box sx={{ flex: 1, mr: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 800, color: '#283593', display: 'block', mb: 0.5, lineHeight: 1.2 }}
            >
              {orderNo ? `№ ${orderNo}` : 'Без № замовлення'}
            </Typography>
            <KitPartsList order={order} compact sx={{ mb: 0.5 }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              {order.machine_identifier} · {order.press_form_identifier || '—'}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            {isPausedMaint
              ? <Chip label="Зупинено на обслуговування" size="small" sx={{ bgcolor: '#4a148c', color: '#fff', fontWeight: 700 }} />
              : <OrderChip status={order.status} />
            }
            {order.status === 'paused' && order.pause_reason && !isPausedMaint && (
              <Typography variant="caption" sx={{ color: '#e65100', display: 'block', fontWeight: 600, mt: 0.25 }}>
                {PAUSE_REASONS.find((r) => r.value === order.pause_reason)?.label || order.pause_reason}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Progress bar for in_progress / paused */}
        {['in_progress', 'paused'].includes(order.status) && (
          <Box sx={{ mb: 1.25 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                Виготовлено: <b>{produced}</b> / {total} шт
              </Typography>
              {onSchedule !== null && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {onSchedule
                    ? <TrendingUpIcon sx={{ fontSize: 14, color: '#2e7d32' }} />
                    : <TrendingDownIcon sx={{ fontSize: 14, color: '#c62828' }} />
                  }
                  <Typography variant="caption" sx={{ color: onSchedule ? '#2e7d32' : '#c62828', fontWeight: 600 }}>
                    {onSchedule ? 'Вчасно' : 'Відстає'}
                  </Typography>
                </Box>
              )}
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8, borderRadius: 3,
                bgcolor: '#e0e0e0',
                '& .MuiLinearProgress-bar': { bgcolor: onSchedule === false ? '#ef5350' : '#42a5f5', borderRadius: 3 },
              }}
            />
          </Box>
        )}

        {mixLine && (
          <Typography
            variant="body2"
            sx={{
              color: '#4e342e', fontWeight: 700, fontSize: '0.82rem', lineHeight: 1.35,
              bgcolor: '#fff8e1', border: '1px solid #ffe082', borderRadius: 1.5,
              px: 1.25, py: 0.85, mb: 1, userSelect: 'none', pointerEvents: 'none',
            }}
          >
            {mixLine}
          </Typography>
        )}

        {realtimeCount !== null && (
          <Typography variant="caption" sx={{ color: '#1b5e20', fontWeight: 600, display: 'block' }}>
            ● Реал-тайм: {realtimeCount.toLocaleString('uk-UA')} шт
          </Typography>
        )}
        {order.status === 'planned' && (
          <Typography variant="caption" color="text.secondary">
            Заплановано: {total} шт · Старт: {fmtDt(order.planned_start)}
          </Typography>
        )}

        {/* Remaining & schedule */}
        <Box sx={{ display: 'flex', gap: 2, mt: 0.75, flexWrap: 'wrap' }}>
          {['in_progress', 'paused'].includes(order.status) && (
            <Typography variant="caption" color="text.secondary">
              Залишилось: <b>{remaining} шт</b>
            </Typography>
          )}
          {order.planned_end && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <AccessTimeIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">до {fmtDt(order.planned_end)}</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Maintenance banner */}
      {maintTicket && maintTicket.scheduled_at && !isPausedMaint && (
        <Box sx={{ mx: 2.5, mb: 1, bgcolor: '#f3e5f5', border: '1px solid #ce93d8', borderRadius: 1.5, px: 1.5, py: 0.75 }}>
          <Typography variant="caption" fontWeight={700} color="#6a1b9a" display="flex" alignItems="center" gap={0.5}>
            <BuildIcon sx={{ fontSize: 13 }} />
            Обслуговування о {fmtTime(maintTicket.scheduled_at)}
          </Typography>
        </Box>
      )}

      {/* Maintenance stop button / resume */}
      {isPausedMaint ? (
        <Box sx={{ px: 2.5, pb: 2.5 }}>
          <Button
            fullWidth variant="contained" size="medium"
            startIcon={<PlayArrowIcon />}
            onClick={(e) => { e.stopPropagation(); onMaintStop && onMaintStop('resume', order); }}
            sx={{ bgcolor: '#1565c0', borderRadius: 2, py: 1.25, fontWeight: 700, textTransform: 'none', fontSize: '0.9rem' }}
          >
            Відновити роботу
          </Button>
        </Box>
      ) : maintTicket && !isPausedMaint && order.status === 'in_progress' && (
        <Box sx={{ px: 2.5, pb: 2.5 }}>
          <Button
            fullWidth variant="contained" size="medium"
            startIcon={<BuildIcon />}
            onClick={(e) => { e.stopPropagation(); onMaintStop && onMaintStop('stop', order, maintTicket); }}
            sx={{ bgcolor: '#6a1b9a', borderRadius: 2, py: 1.25, fontWeight: 700, textTransform: 'none', fontSize: '0.9rem' }}
          >
            Зупинити на обслуговування
          </Button>
        </Box>
      )}

      {/* Action button — без «Зупинити» назовні */}
      {!isPausedMaint && !maintTicket && cfg && action !== 'complete' && (() => {
        const mbInfo = order.mixing_batch_info;
        const mixCanStart = !mbInfo || mbInfo.can_start === true || mbInfo.status === 'done'
          || (Number(mbInfo.covered_units) >= 1);
        const mixBlocked = action === 'start' && mbInfo && !mixCanStart;
        const tileDisabled = busy || cfg.disabled || mixBlocked;
        return (
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <Button
              fullWidth variant="contained" size="medium"
              startIcon={busy ? <CircularProgress size={16} sx={{ color: 'white' }} /> : cfg.icon}
              disabled={tileDisabled}
              onClick={(e) => { e.stopPropagation(); if (!tileDisabled) onAction(action, order); }}
              sx={{
                bgcolor: tileDisabled ? '#bdbdbd' : cfg.bg, borderRadius: 2, py: 1.25, fontWeight: 700,
                textTransform: 'none', fontSize: '0.9rem',
                '&:hover': { bgcolor: tileDisabled ? '#bdbdbd' : cfg.bg, filter: tileDisabled ? 'none' : 'brightness(0.88)' },
              }}
            >
              {cfg.label}
            </Button>
            {mixBlocked && (
              <Typography variant="caption" color="warning.main" display="block" textAlign="center" sx={{ mt: 0.5 }}>
                Додайте заміс хоча б на 1 шт
              </Typography>
            )}
            {action === 'drying' && (
              <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 0.5 }}>
                Сушіння в процесі…
              </Typography>
            )}
            {action === 'awaiting_setup' && (
              <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 0.5 }}>
                Очікує завершення наладки від наладчика
              </Typography>
            )}
          </Box>
        );
      })()}
      {/* Замість «Зупинити» — «Провести наладку» (повторна) для запущених ЗВ */}
      {!isPausedMaint && !maintTicket && action === 'complete' && (
        <Box sx={{ px: 2.5, pb: 2.5 }}>
          <Button
            fullWidth variant="contained" size="medium"
            startIcon={busy ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <BuildIcon />}
            disabled={busy}
            onClick={(e) => { e.stopPropagation(); onRedoSetup && onRedoSetup(order); }}
            sx={{ bgcolor: '#ef6c00', borderRadius: 2, py: 1.25, fontWeight: 700, textTransform: 'none', fontSize: '0.9rem', '&:hover': { bgcolor: '#e65100' } }}
          >
            Провести наладку
          </Button>
        </Box>
      )}
    </Paper>
  );
}

// ─── Order Detail Dialog ───────────────────────────────────────────────────────
function OrderDetailDialog({ order, open, onClose, dryingRows, onAction, onPause, onResume, busy, onRedoSetup, maintTicket, onMaintStop }) {
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    if (!open || !order?.needs_drying || order.drying_status !== 'in_progress') return undefined;
    const id = setInterval(() => setNowTick(Date.now()), 15000);
    return () => clearInterval(id);
  }, [open, order?.id, order?.needs_drying, order?.drying_status]);
  // keep nowTick referenced so drying countdown re-evaluates
  void nowTick;
  const [pwStock, setPwStock] = React.useState([]);
  const [pwStockLoaded, setPwStockLoaded] = React.useState(false);
  React.useEffect(() => {
    if (open && order?.pause_reason === 'no_material' && order?.order_materials?.length > 0) {
      setPwStockLoaded(false);
      api.get('/production-warehouse/stock/', { params: { page_size: 500 } })
        .then(r => { setPwStock(r.data?.results || r.data || []); setPwStockLoaded(true); })
        .catch(() => { setPwStock([]); setPwStockLoaded(true); });
    } else {
      setPwStock([]);
      setPwStockLoaded(false);
    }
  }, [open, order?.id, order?.pause_reason]);

  if (!order) return null;
  const { produced, total, remaining, onSchedule, expectedNow, progress } = scheduleInfo(order);
  const action = getNextAction(order);
  const cfg = action ? ACTION_CFG[action] : null;
  const orderDrying = dryingRows.filter((r) => r.order_id === order.id || r.order === order.id);

  const packagingRows = order.packaging_rows || [];
  const packagingNote = order.packaging_note || '';
  const hasPackaging = packagingRows.length > 0 || packagingNote;

  // Дані замісу
  const mb = order.mixing_batch_info;
  const totalRequired = mb?.total_required_kg || 0;
  const totalAdded = mb?.total_added_kg || 0;
  // Скільки виробів вистачить на наявний заміс (з бекенду — по найвужчому рядку)
  const itemsPerKg = totalRequired > 0 ? (order.quantity || 0) / totalRequired : 0;
  const itemsCoveredByMix = mb?.covered_units != null
    ? Number(mb.covered_units)
    : (totalAdded > 0 && itemsPerKg > 0 ? Math.floor(totalAdded * itemsPerKg) : 0);
  const mixCanStart = !mb || mb.can_start === true || mb.status === 'done' || itemsCoveredByMix >= 1;
  // Час виготовлення: якщо є planned_start і planned_end — рахуємо секунди на штуку
  const plannedDurationMs = order.planned_end && order.planned_start
    ? new Date(order.planned_end) - new Date(order.planned_start) : 0;
  const secsPerItem = plannedDurationMs > 0 && order.quantity > 0 ? plannedDurationMs / order.quantity / 1000 : 0;
  const coveredHours = itemsCoveredByMix > 0 && secsPerItem > 0
    ? (itemsCoveredByMix * secsPerItem / 3600).toFixed(1) : null;

  const mixingDetail = mb ? (
    mb.status === 'done'
      ? `Готово: ${totalAdded} кг з ${totalRequired} кг · вистачить на ${itemsCoveredByMix} шт${coveredHours ? ` (~${coveredHours} год)` : ''}`
      : totalAdded > 0
        ? `В процесі: додано ${totalAdded} кг з ${totalRequired} кг · поки вистачить на ${itemsCoveredByMix} шт${coveredHours ? ` (~${coveredHours} год)` : ''}${mixCanStart ? ' · можна стартувати' : ''}`
        : `Потрібно: ${totalRequired} кг — ще не змішували`
  ) : null;

  const dryStartAt = dryingShouldStartAt(order);
  const dryStartHint = dryStartAt
    ? `Почати сушіння о ${dryStartAt.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })} (план ${order.planned_start ? new Date(order.planned_start).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' }) : '—'} − ${order.drying_time_minutes} хв)`
    : null;
  const dryingDone = isOrderDryingDone(order);
  const dryingProcessStatus = !order.needs_drying
    ? 'n/a'
    : (dryingDone || order.drying_status === 'completed')
      ? 'done'
      : order.drying_status === 'in_progress'
        ? 'in_progress'
        : 'pending';
  const dryingDetail = !order.needs_drying
    ? 'Сушіння не потрібне'
    : (() => {
      const lines = [];
      if (dryStartHint && order.drying_status !== 'completed') lines.push(dryStartHint);
      if (order.drying_status === 'completed') {
        lines.push(`Посушено ✓ (${order.drying_time_minutes || '?'} хв)`);
      } else if (order.drying_status === 'in_progress' && order.drying_started_at) {
        const finishAt = new Date(new Date(order.drying_started_at).getTime() + Number(order.drying_time_minutes || 0) * 60000);
        const diffMin = Math.round((finishAt - nowTick) / 60000);
        const timeStr = finishAt.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
        lines.push(diffMin > 0
          ? `Сушиться → готово ~${timeStr} (через ${diffMin} хв)`
          : `Сушіння завершено за таймером (${timeStr}) — можна проводити наладку`);
      } else {
        lines.push(`Очікує старту оператора · ${order.drying_time_minutes || '?'} хв`);
      }
      return lines.join('\n');
    })();

  const processes = [
    {
      label: 'Підготовка замісу',
      icon: '🧪',
      status: mb
        ? (mb.status === 'done' ? 'done' : totalAdded > 0 ? 'in_progress' : 'pending')
        : (order.status !== 'planned' ? 'n/a' : 'pending'),
      detail: mixingDetail || '—',
    },
    {
      label: 'Сушіння матеріалів',
      icon: '💧',
      status: dryingProcessStatus,
      detail: dryingDetail,
    },
    // «Виготовлення» не показуємо — це кінцева мета, не підготовчий етап
    ...(hasPackaging ? [{
      label: 'Пакування',
      icon: '📦',
      status: order.status === 'completed' ? 'done' : order.status === 'in_progress' ? 'pending' : 'n/a',
      detail: packagingNote || null,
      rows: packagingRows,
    }] : []),
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a237e', color: 'white', px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#90caf9', lineHeight: 1.2, mb: 0.25 }}>
              {(order.customer_order_number != null && String(order.customer_order_number).trim() !== '')
                ? `№ ${String(order.customer_order_number).trim()}`
                : 'Без № замовлення'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              {order.machine_identifier} · {order.press_form_identifier || '—'}
            </Typography>
            <KitPartsList
              order={order}
              showProduced={['in_progress', 'paused', 'completed'].includes(order.status)}
              sx={{
                mt: 0.25,
                '& .MuiTypography-root': { color: 'inherit' },
                '& .MuiTypography-caption': { color: 'rgba(255,255,255,0.85) !important' },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <OrderChip status={order.status} size="medium" />
            <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
          </Box>
        </Box>

        {/* Progress in header */}
        {order.status === 'in_progress' && (
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Виготовлено: <b>{produced}</b> / {total} шт
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {progress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate" value={progress}
              sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.2)', '& .MuiLinearProgress-bar': { bgcolor: onSchedule === false ? '#ef9a9a' : '#a5d6a7', borderRadius: 4 } }}
            />
          </Box>
        )}
      </Box>

      <DialogContent sx={{ pt: 2.5, pb: 1 }}>

        {/* Key metrics */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 2.5 }}>
          <MetricCard label="Всього потрібно" value={`${total} шт`} />
          <MetricCard label="Виготовлено" value={`${produced} шт`} highlight={produced > 0} />
          <MetricCard label="Залишилось" value={`${remaining} шт`} warn={remaining > 0 && order.status === 'in_progress'} />
        </Box>

        {/* Schedule */}
        {order.status === 'in_progress' && onSchedule !== null && (
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, mb: 2.5, borderColor: onSchedule ? '#a5d6a7' : '#ef9a9a', bgcolor: onSchedule ? '#f1f8e9' : '#fbe9e7' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {onSchedule
                ? <TrendingUpIcon sx={{ color: '#2e7d32' }} />
                : <TrendingDownIcon sx={{ color: '#c62828' }} />
              }
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: onSchedule ? '#2e7d32' : '#c62828' }}>
                  {onSchedule ? 'Вкладаємось у графік' : 'Відстаємо від графіку'}
                </Typography>
                {expectedNow != null && (
                  <Typography variant="caption" color="text.secondary">
                    За планом зараз мало бути: {expectedNow} шт · Фактично: {produced} шт
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        )}

        {/* Time info */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Початок</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtDt(order.planned_start)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Дедлайн</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtDt(order.planned_end)}</Typography>
          </Box>
          {order.launched_at && (
            <Box>
              <Typography variant="caption" color="text.secondary">Запущено</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtDt(order.launched_at)}</Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Process steps */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Процеси</Typography>
        {processes.map((p) => (
          <Box key={p.label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
            <Box sx={{ fontSize: 20, lineHeight: 1.4 }}>{p.icon}</Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.label}</Typography>
                <ProcessChip status={p.status} />
              </Box>
              {p.detail && p.detail !== '—' && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ whiteSpace: 'pre-line' }}>{p.detail}</Typography>
              )}
              {/* Packaging type tags */}
              {p.rows && p.rows.length > 0 && (
                <Box sx={{ mt: 0.5, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {p.rows.some((r) => r.pack_at_machine) && (
                    <Chip label="На ТПА" size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#e3f2fd', color: '#0d47a1' }} />
                  )}
                  {p.rows.some((r) => !r.pack_at_machine) && (
                    <Chip label="На пакуванні" size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#fce4ec', color: '#880e4f' }} />
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    {p.rows.length} позиція(-й)
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        ))}

        {/* ── Блок нестачі матеріалів ── */}
        {order.pause_reason === 'no_material' && order.order_materials?.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#b71c1c', display: 'flex', alignItems: 'center', gap: 0.75 }}>
              ⚠️ Матеріали для замовлення
            </Typography>
            {order.order_materials.map((mat) => {
              const name = mat.raw_material_name || mat.dye_name || `#${mat.id}`;
              const required = mat.quantity_required || 0;
              const stock = pwStock.find(s =>
                (mat.raw_material && s.raw_material === mat.raw_material) ||
                (mat.dye && s.dye === mat.dye)
              );
              const available = stock?.quantity ?? null;
              const mixDone = order.mixing_batch_info?.status === 'done';
              // Готовий заміс покриває компоненти — не показуємо фальшиву нестачу по PWS
              const isSufficient = mixDone || (available !== null && available >= required);
              const isShort = !mixDone && available !== null && available < required;
              return (
                <Box key={mat.id} sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  px: 1.5, py: 1, mb: 0.75, borderRadius: 2,
                  bgcolor: isShort ? '#fbe9e7' : isSufficient ? '#f1f8e9' : pwStockLoaded ? '#fbe9e7' : '#fff8e1',
                  border: `1px solid ${isShort ? '#ef9a9a' : isSufficient ? '#a5d6a7' : pwStockLoaded ? '#ef9a9a' : '#ffe082'}`,
                }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Потрібно: <b>{required} кг</b>
                      {mixDone && (
                        <> · <b style={{ color: '#2e7d32' }}>покрито замісом</b></>
                      )}
                      {!mixDone && available !== null && (
                        <> · На складі виробництва: <b style={{ color: isShort ? '#b71c1c' : '#2e7d32' }}>{available} кг</b></>
                      )}
                      {!mixDone && available === null && !pwStockLoaded && <> · <span style={{ color: '#9e9e9e' }}>перевіряємо склад…</span></>}
                      {!mixDone && available === null && pwStockLoaded && <> · <span style={{ color: '#b71c1c' }}>немає на складі виробництва</span></>}
                    </Typography>
                  </Box>
                  {mixDone && <Chip label="У замісі" size="small" sx={{ bgcolor: '#2e7d32', color: '#fff', fontWeight: 700, fontSize: '0.7rem' }} />}
                  {!mixDone && isShort && <Chip label={`Не вистачає ${(required - available).toFixed(2)} кг`} size="small" sx={{ bgcolor: '#b71c1c', color: '#fff', fontWeight: 700, fontSize: '0.7rem' }} />}
                  {!mixDone && isSufficient && <Chip label="Достатньо" size="small" sx={{ bgcolor: '#2e7d32', color: '#fff', fontWeight: 700, fontSize: '0.7rem' }} />}
                  {!mixDone && available === null && pwStockLoaded && <Chip label="Відсутній" size="small" sx={{ bgcolor: '#b71c1c', color: '#fff', fontWeight: 700, fontSize: '0.7rem' }} />}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      {/* Maintenance scheduled time banner */}
      {maintTicket && maintTicket.scheduled_at && order.status !== 'paused' && (
        <Box sx={{ mx: 3, mb: 1, bgcolor: '#f3e5f5', border: '1px solid #ce93d8', borderRadius: 2, px: 2, py: 1 }}>
          <Typography variant="body2" fontWeight={700} color="#6a1b9a" display="flex" alignItems="center" gap={0.75}>
            <BuildIcon sx={{ fontSize: 16 }} />
            Заплановане обслуговування о {fmtTime(maintTicket.scheduled_at)}
          </Typography>
          {maintTicket.reason && (
            <Typography variant="caption" color="#7b1fa2" sx={{ mt: 0.25, display: 'block' }}>
              {maintTicket.reason}
            </Typography>
          )}
        </Box>
      )}

      <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', gap: 1 }}>
        {order.status === 'paused' && order.pause_reason === 'maintenance' ? (
          <>
            <Box sx={{ bgcolor: '#f3e5f5', border: '1px solid #ce93d8', borderRadius: 2, px: 2, py: 1.25, mb: 0.5, width: '100%', textAlign: 'center' }}>
              <Typography variant="body2" fontWeight={700} color="#6a1b9a">
                🔧 Машина зупинена на обслуговування
              </Typography>
            </Box>
            <Button
              fullWidth variant="contained" size="large"
              startIcon={<PlayArrowIcon />}
              onClick={() => { onMaintStop && onMaintStop('resume', order); onClose(); }}
              sx={{ bgcolor: '#1565c0', borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none' }}
            >
              Відновити роботу
            </Button>
          </>
        ) : order.status === 'paused' ? (() => {
          const mixingProcess = processes.find((p) => p.label === 'Підготовка замісу');
          const dryingProcess = processes.find((p) => p.label === 'Сушіння матеріалів');
          const mixingBlockedResume = mixingProcess && mixingProcess.status === 'pending';
          const dryingBlockedResume = dryingProcess && dryingProcess.status !== 'done' && dryingProcess.status !== 'n/a';
          const resumeBlockers = [
            mixingBlockedResume && 'Підготовка замісу ще не розпочата — заміс потрібно підготувати перед відновленням',
            dryingBlockedResume && 'Сушіння матеріалів ще не завершено — дочекайтесь готовності',
          ].filter(Boolean);
          const isResumeBlocked = resumeBlockers.length > 0;
          return (
            <Box sx={{ width: '100%' }}>
              {isResumeBlocked && (
                <Box sx={{ bgcolor: '#fff3e0', border: '1px solid #ffb74d', borderRadius: 2, px: 2, py: 1.25, mb: 1.5 }}>
                  <Typography variant="caption" color="#e65100" fontWeight={700} display="block" mb={0.5}>
                    ⚠️ Не можна відновити виробництво:
                  </Typography>
                  {resumeBlockers.map((b) => (
                    <Typography key={b} variant="caption" color="#bf360c" display="block">• {b}</Typography>
                  ))}
                </Box>
              )}
              <Button
                fullWidth variant="contained" size="large"
                startIcon={<PlayArrowIcon />}
                disabled={isResumeBlocked}
                onClick={() => { onResume(order); onClose(); }}
                sx={{ bgcolor: isResumeBlocked ? '#bdbdbd' : '#1565c0', borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none' }}
              >
                Відновити роботу
              </Button>
            </Box>
          );
        })() : maintTicket && order.status === 'in_progress' ? (
          <Button
            fullWidth variant="contained" size="large"
            startIcon={<BuildIcon />}
            onClick={() => { onMaintStop && onMaintStop('stop', order, maintTicket); onClose(); }}
            sx={{ bgcolor: '#6a1b9a', borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none' }}
          >
            Зупинити на обслуговування
          </Button>
        ) : cfg && action !== 'complete' && (() => {
          const mixingProcess = processes.find((p) => p.label === 'Підготовка замісу');
          const dryingProcess = processes.find((p) => p.label === 'Сушіння матеріалів');
          // «Почати роботу» — якщо замісу вистачає хоча б на 1 шт
          const mixingBlockedStart = action === 'start' && mb && !mixCanStart;
          // Launch: сушіння має бути завершене (або n/a)
          const mixingBlockedLaunch = action === 'launch' && mixingProcess && mixingProcess.status === 'pending';
          const dryingBlockedLaunch = action === 'launch' && dryingProcess && dryingProcess.status !== 'done' && dryingProcess.status !== 'n/a';
          const blockers = [
            mixingBlockedStart && 'Замісу ще не вистачає на 1 шт — додайте матеріали на лінії змішування',
            mixingBlockedLaunch && 'Підготовка замісу ще не розпочата',
            dryingBlockedLaunch && 'Сушіння матеріалів ще не завершено — дочекайтесь готовності',
            action === 'drying' && 'Сушіння в процесі — після таймера зʼявиться кнопка «Провести наладку»',
          ].filter(Boolean);
          const isBlocked = blockers.length > 0 || Boolean(cfg.disabled);
          return (
            <Box sx={{ width: '100%' }}>
              {action === 'start' && dryStartHint && (
                <Box sx={{ bgcolor: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 2, px: 2, py: 1.25, mb: 1.5 }}>
                  <Typography variant="caption" color="#0d47a1" fontWeight={700} display="block">
                    💧 {dryStartHint}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    «Почати роботу» запускає таймер сушіння замісу (не викликає наладку).
                  </Typography>
                </Box>
              )}
              {action === 'drying' && dryingDetail && (
                <Box sx={{ bgcolor: '#e1f5fe', border: '1px solid #4fc3f7', borderRadius: 2, px: 2, py: 1.25, mb: 1.5 }}>
                  <Typography variant="caption" color="#01579b" fontWeight={700} display="block" sx={{ whiteSpace: 'pre-line' }}>
                    {dryingDetail}
                  </Typography>
                </Box>
              )}
              {action === 'request_setup' && (
                <Box sx={{ bgcolor: '#fff8e1', border: '1px solid #ffcc80', borderRadius: 2, px: 2, py: 1.25, mb: 1.5 }}>
                  <Typography variant="caption" color="#e65100" fontWeight={700} display="block">
                    Сушіння завершено. Натисніть «Провести наладку», щоб викликати відділ наладки.
                  </Typography>
                </Box>
              )}
              {isBlocked && action !== 'drying' && action !== 'awaiting_setup' && (
                <Box sx={{ bgcolor: '#fff3e0', border: '1px solid #ffb74d', borderRadius: 2, px: 2, py: 1.25, mb: 1.5 }}>
                  <Typography variant="caption" color="#e65100" fontWeight={700} display="block" mb={0.5}>
                    ⚠️ Кнопка заблокована:
                  </Typography>
                  {blockers.map((b) => (
                    <Typography key={b} variant="caption" color="#bf360c" display="block">• {b}</Typography>
                  ))}
                </Box>
              )}
              {action === 'drying' && (
                <Box sx={{ bgcolor: '#fff3e0', border: '1px solid #ffb74d', borderRadius: 2, px: 2, py: 1.25, mb: 1.5 }}>
                  <Typography variant="caption" color="#e65100" fontWeight={700} display="block">
                    ⚠️ Сушіння в процесі — після таймера зʼявиться кнопка «Провести наладку»
                  </Typography>
                </Box>
              )}
              <Button
                fullWidth variant="contained" size="large"
                startIcon={busy ? <CircularProgress size={18} sx={{ color: 'white' }} /> : cfg.icon}
                disabled={busy || isBlocked}
                onClick={() => { onAction(action, order); onClose(); }}
                sx={{ bgcolor: isBlocked ? '#bdbdbd' : cfg.bg, borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none' }}
              >
                {cfg.label}
              </Button>
            </Box>
          );
        })()}
        {order.status === 'in_progress' && (
          <Button
            fullWidth variant="outlined" size="large"
            onClick={() => { onPause(order); }}
            sx={{ borderColor: '#e65100', color: '#e65100', borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none',
              '&:hover': { bgcolor: '#fff3e0', borderColor: '#e65100' } }}
          >
            ⏸ Зупинити
          </Button>
        )}
        {order.status === 'in_progress' && (
          <Button
            fullWidth variant="text" size="small"
            startIcon={<BuildIcon sx={{ fontSize: 14 }} />}
            onClick={() => { onRedoSetup(order); onClose(); }}
            sx={{ color: '#757575', textTransform: 'none', fontSize: 12, fontWeight: 600,
              '&:hover': { bgcolor: '#f5f5f5', color: '#424242' } }}
          >
            Провести повторну наладку
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function MetricCard({ label, value, highlight, warn }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5, textAlign: 'center', bgcolor: highlight ? '#e8f5e9' : warn ? '#fff8e1' : '#fafafa' }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: highlight ? '#2e7d32' : warn ? '#f57f17' : 'inherit' }}>
        {value}
      </Typography>
    </Paper>
  );
}

function ProcessChip({ status }) {
  const map = {
    done: { label: '✓ Готово', bg: '#e8f5e9', color: '#2e7d32' },
    in_progress: { label: '● В процесі', bg: '#e3f2fd', color: '#1565c0' },
    pending: { label: '○ Очікує', bg: '#fff3e0', color: '#e65100' },
    'n/a': { label: '— Н/Д', bg: '#f5f5f5', color: '#9e9e9e' },
    planned: { label: '○ Заплановано', bg: '#e3f2fd', color: '#0d47a1' },
    completed: { label: '✓ Завершено', bg: '#e8f5e9', color: '#2e7d32' },
    є: { label: '● Є', bg: '#e3f2fd', color: '#1565c0' },
  };
  const cfg = map[status] || { label: status, bg: '#eee', color: '#555' };
  return <Chip label={cfg.label} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, height: 20, fontSize: '0.7rem' }} />;
}

// ─── TPA Queue Dialog ─────────────────────────────────────────────────────────
function TpaQueueDialog({ machine, orders, open, onClose, onAction, busyOrderId }) {
  if (!machine) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ bgcolor: '#1a237e', color: 'white', px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PrecisionManufacturingIcon />
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {machine.identifier || machine.name}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </Box>
      <DialogContent sx={{ pt: 2 }}>
        {orders.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: '#a5d6a7', mb: 1 }} />
            <Typography color="text.secondary">Немає активних замовлень</Typography>
          </Box>
        ) : orders.map((o, idx) => {
          const { produced, total, onSchedule } = scheduleInfo(o);
          const action = getNextAction(o);
          const cfg = action ? ACTION_CFG[action] : null;
          const kit = getOrderKitParts(o);
          return (
            <Paper key={o.id} variant="outlined" sx={{ borderRadius: 2, p: 2, mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">#{idx + 1} у черзі</Typography>
                  {o.customer_order_number && (
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#5c6bc0', display: 'block' }}>
                      № {o.customer_order_number}
                    </Typography>
                  )}
                  <KitPartsList order={o} compact />
                </Box>
                <OrderChip status={o.status} />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {kit.isKit ? `${kit.parts.length} поз.` : `${total} шт`}
                {' · '}{fmtDt(o.planned_start)} → {fmtDt(o.planned_end)}
              </Typography>
              {o.status === 'in_progress' && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Виготовлено: {produced}/{total} шт
                  {onSchedule !== null && (onSchedule ? ' ▲ вчасно' : ' ▼ відстає')}
                </Typography>
              )}
              {cfg && (
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    fullWidth variant="contained" size="medium"
                    startIcon={busyOrderId === o.id ? <CircularProgress size={14} sx={{ color: 'white' }} /> : cfg.icon}
                    disabled={busyOrderId === o.id}
                    onClick={() => { onAction(action, o); onClose(); }}
                    sx={{ bgcolor: cfg.bg, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                  >
                    {cfg.label}
                  </Button>
                </Box>
              )}
            </Paper>
          );
        })}
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const ORDER_FILTERS = [
  { value: 'active',    label: 'Активні' },
  { value: 'all',       label: 'Усі' },
  { value: 'planned',   label: 'Заплановані' },
  { value: 'in_progress', label: 'В роботі' },
  { value: 'completed', label: 'Завершені' },
];

export default function OperatorPageV2({ currentUser }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyOrderId, setBusyOrderId] = useState(null);
  const [processDeclarationOpen, setProcessDeclarationOpen] = useState(false);
  const [processDeclarationOrder, setProcessDeclarationOrder] = useState(null);
  const [processDeclarations, setProcessDeclarations] = useState({});
  const [operatorUser, setOperatorUser] = useState(null);
  const [dryingRows, setDryingRows] = useState([]);
  const [orderFilter, setOrderFilter] = useState('active');
  const [mainView, setMainView] = useState('orders'); // 'tpa' | 'orders'
  const isMobile = useMediaQuery('(max-width:600px)');

  // Dialogs
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  const [tpaDialogMachine, setTpaDialogMachine] = useState(null);
  const [tpaDialogOrders, setTpaDialogOrders] = useState([]);
  const [tpaDialogOpen, setTpaDialogOpen] = useState(false);

  // QC
  const [pendingQcChecks, setPendingQcChecks] = useState([]);
  const [currentQcCheck, setCurrentQcCheck] = useState(null);
  const [qcCompleteComment, setQcCompleteComment] = useState('');
  const [qcCompletePassed, setQcCompletePassed] = useState(true);
  const [qcCompleteSubmitting, setQcCompleteSubmitting] = useState(false);
  const [qcNoPendingOpen, setQcNoPendingOpen] = useState(false);
  const [qcNoPendingSubmitting, setQcNoPendingSubmitting] = useState(false);
  const [qcMenuAnchor, setQcMenuAnchor] = useState(null);
  const [callQcDialogOpen, setCallQcDialogOpen] = useState(false);
  const [callQcOrderId, setCallQcOrderId] = useState('');
  const [callQcSubmitting, setCallQcSubmitting] = useState(false);
  const [callQcActiveOrders, setCallQcActiveOrders] = useState([]);

  // Assignment popup
  const [assignedOrderPopup, setAssignedOrderPopup] = useState(null);
  const [pendingAssignments, setPendingAssignments] = useState([]);
  const [declineMode, setDeclineMode] = useState(false);
  const [declineReasonInput, setDeclineReasonInput] = useState('');
  const [popupSubmitting, setPopupSubmitting] = useState(false);
  const respondedOrderIdsRef = useRef(new Set(
    (() => { try { return JSON.parse(localStorage.getItem('op_responded_orders') || '[]'); } catch { return []; } })()
  ));
  const shownOrderIdRef = useRef(null);

  // Shift report — ключ прив'язаний до userId + дати, щоб скидатись при виході / новому дні
  const shiftReportKey = `op_shift_report_${currentUser?.id}_${new Date().toDateString()}`;
  const [shiftReportOpen, setShiftReportOpen] = useState(false);
  const [shiftReportLines, setShiftReportLines] = useState([]);
  const [shiftReportStep, setShiftReportStep] = useState(0);
  const [shiftReportSubmitting, setShiftReportSubmitting] = useState(false);
  const [shiftReportDone, setShiftReportDone] = useState(false);
  const [shiftReportLoading, setShiftReportLoading] = useState(false);
  /** machineId -> string value of TPA counter at shift end */
  const [tpaCounters, setTpaCounters] = useState({});
  const [efficiencyReasons, setEfficiencyReasons] = useState([]);
  const [efficiencyPreview, setEfficiencyPreview] = useState(null);
  const [efficiencyLowReason, setEfficiencyLowReason] = useState('');
  const [efficiencyLowComment, setEfficiencyLowComment] = useState('');
  const [efficiencyPreviewLoading, setEfficiencyPreviewLoading] = useState(false);
  /** machineId -> { last_counter_reading, machine_identifier } */
  const [tpaCounterHints, setTpaCounterHints] = useState({});
  const [lastSubmittedReport, setLastSubmittedReport] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(`op_shift_report_${currentUser?.id}_${new Date().toDateString()}`) || 'null'); } catch { return null; }
  });
  const [shiftReportAlreadySubmittedOpen, setShiftReportAlreadySubmittedOpen] = useState(false);

  // Machines assigned by manager via DailyMachineOperator
  const [assignedMachineIds, setAssignedMachineIds] = useState(null); // null = loading

  // Setup, complete, start-late dialogs
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupWastes, setSetupWastes] = useState([]);
  const [setupTarget, setSetupTarget] = useState(null);
  const [setupSubmitting, setSetupSubmitting] = useState(false);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [pauseTarget, setPauseTarget] = useState(null);
  const [pauseReason, setPauseReason] = useState('');
  const [pauseSubmitting, setPauseSubmitting] = useState(false);
  const [endLateOpen, setEndLateOpen] = useState(false);
  const [endLateTarget, setEndLateTarget] = useState(null);
  const [endLateReason, setEndLateReason] = useState('');
  const [crushingWasteKg, setCrushingWasteKg] = useState('0');
  const [nonRecoverableWasteKg, setNonRecoverableWasteKg] = useState('');
  const [completeOrderLate, setCompleteOrderLate] = useState(false);
  const [endLateSubmitting, setEndLateSubmitting] = useState(false);
  const [startLateOpen, setStartLateOpen] = useState(false);
  const [startLateTarget, setStartLateTarget] = useState(null);
  const [startLateReason, setStartLateReason] = useState('');
  const [startLateSubmitting, setStartLateSubmitting] = useState(false);
  const [packagingWarn, setPackagingWarn] = useState(null);
  const [coProductsWarn, setCoProductsWarn] = useState(null); // { order, performerName, detail, names, startPayload }
  const [redoSetupDialog, setRedoSetupDialog] = useState(null); // order object // { order, performerName, items: [] }

  // ── Stock transfer to production warehouse ───────────
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferStockItems, setTransferStockItems] = useState([]);
  const [transferSelected, setTransferSelected] = useState(null);
  const [transferQty, setTransferQty] = useState('');
  const [transferNote, setTransferNote] = useState('');
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [transferError, setTransferError] = useState('');

  // ── Maintenance ──────────────────────────────────────
  const [maintTickets, setMaintTickets] = useState([]);
  const [maintPopup, setMaintPopup] = useState(null); // ticket that needs popup

  // ── Shift Handover ───────────────────────────────────
  const [handoverDialogOpen, setHandoverDialogOpen] = useState(false);
  const [handoverSubmitting, setHandoverSubmitting] = useState(false);

  // ── Derived ─────────────────────────────────────────
  const userForOp = operatorUser || currentUser;

  const allMachines = useMemo(() => {
    if (!userForOp) return [];
    return userForOp.machines_data?.filter((m) => typeof m === 'object') || [];
  }, [userForOp]);

  // Машини, призначені керівником на сьогодні
  const myMachines = useMemo(() => {
    if (assignedMachineIds === null) return []; // ще завантажуємо
    if (assignedMachineIds.size === 0) return allMachines; // нічого не призначено — показуємо всі
    return allMachines.filter((m) => assignedMachineIds.has(Number(m.id)));
  }, [allMachines, assignedMachineIds]);

  const operatorMachineIds = useMemo(() => myMachines.map((m) => Number(m.id)), [myMachines]);

  const filteredOrders = useMemo(() => {
    let base = orders.filter((o) =>
      operatorMachineIds.length === 0 || operatorMachineIds.includes(Number(o.machine))
    );
    if (orderFilter === 'active') return base.filter((o) => ['planned', 'in_progress', 'paused'].includes(o.status));
    if (orderFilter === 'planned') return base.filter((o) => o.status === 'planned');
    if (orderFilter === 'in_progress') return base.filter((o) => o.status === 'in_progress');
    if (orderFilter === 'completed') return base.filter((o) => o.status === 'completed');
    return base;
  }, [orders, operatorMachineIds, orderFilter]);

  const ordersByMachine = useMemo(() => {
    const map = {};
    orders.forEach((o) => {
      if (!map[o.machine]) map[o.machine] = [];
      map[o.machine].push(o);
    });
    return map;
  }, [orders]);

  // ── Data loading ─────────────────────────────────────
  const loadOrders = useCallback(async () => {
    try {
      const params = {
        page_size: 200,
        light: 1,
        status_in: 'planned,in_progress,paused',
      };
      if (operatorMachineIds.length > 0) {
        params.machines = operatorMachineIds.join(',');
      }
      const r = await productionOrderService.getAll(params);
      setOrders(r.data?.results || r.data || []);
    } catch {}
    setLoading(false);
  }, [operatorMachineIds]);

  const loadDrying = useCallback(async () => {
    if (operatorMachineIds.length === 0) return;
    try {
      const r = await orderMaterialDryingService.list({ machines: operatorMachineIds.join(','), page_size: 200 });
      setDryingRows(r.data?.results || r.data || []);
    } catch {}
  }, [operatorMachineIds]);

  const loadPendingQc = useCallback(async () => {
    if (operatorMachineIds.length === 0 || !currentUser?.id) return [];
    try {
      const r = await qualityCheckService.getPending({ machines: operatorMachineIds.join(','), operator_id: currentUser.id, auto: 1 });
      const list = r.data?.results || r.data || [];
      setPendingQcChecks(list);
      // Не відкриваємо модалку автоматично — лише оновлюємо бейдж «Контроль якості (N)»
      return list;
    } catch { return []; }
  }, [operatorMachineIds, currentUser?.id]);

  // Maintenance tickets for operator machines
  const loadMaintTickets = useCallback(async () => {
    if (operatorMachineIds.length === 0) return;
    try {
      const r = await maintenanceTicketService.forOperator(operatorMachineIds.join(','));
      const tickets = Array.isArray(r.data) ? r.data : r.data?.results || [];
      setMaintTickets(tickets);
      // Show popup for tickets that need it
      const popupTicket = tickets.find((t) => t.show_popup);
      if (popupTicket) {
        setMaintPopup(popupTicket);
        // Mark notified on server
        maintenanceTicketService.markOperatorNotified(popupTicket.id).catch(() => {});
      }
    } catch {}
  }, [operatorMachineIds]);

  useEffect(() => {
    loadMaintTickets();
    const id = setInterval(loadMaintTickets, 60 * 1000);
    return () => clearInterval(id);
  }, [loadMaintTickets]);

  const handleHandoverSubmit = async () => {
    setHandoverSubmitting(true);
    // Кінець зміни з лічильником створюється при «Подати звіт».
    // Тут лише вихід — без порожнього ShiftHandover (counter обовʼязковий).
    try { /* no-op */ } catch {}
    await authService.logout();
    sessionStorage.clear();
    window.location.href = '/login';
  };

  // Poll operator profile (rare — full staff payload is heavy and freezes UI)
  useEffect(() => {
    if (!currentUser?.id) return undefined;
    let cancelled = false;
    const refresh = () => staffService.getById(currentUser.id).then((r) => { if (!cancelled) setOperatorUser(r.data); }).catch(() => {});
    refresh();
    const t = setInterval(refresh, 120000);
    return () => { cancelled = true; clearInterval(t); };
  }, [currentUser?.id]);

  // Завантажуємо призначені керівником ТПА для поточного оператора на сьогодні
  useEffect(() => {
    if (!currentUser?.id) return;
    const today = new Date().toISOString().slice(0, 10);
    dailyMachineOperatorService.getAll({ date: today, operator: currentUser.id, page_size: 100 })
      .then((r) => {
        const rows = r.data?.results || r.data || [];
        const ids = new Set(rows.map((row) => Number(row.machine)));
        setAssignedMachineIds(ids);
      })
      .catch(() => setAssignedMachineIds(new Set()));
  }, [currentUser?.id]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  useEffect(() => {
    if (operatorMachineIds.length > 0 && !loading) {
      loadPendingQc();
      loadDrying();
    }
  }, [operatorMachineIds.length, loading]);

  // Poll assignments
  useEffect(() => {
    if (!currentUser?.id) return undefined;
    const check = () => {
      productionOrderService.getAll({ assigned_operator: currentUser.id, operator_response: 'pending', page_size: 50 })
        .then((r) => {
          const results = r.data?.results || r.data || [];
          const opSet = new Set(operatorMachineIds);
          const fresh = results.filter((o) => !respondedOrderIdsRef.current.has(o.id) && (opSet.size === 0 || opSet.has(Number(o.machine))));
          if (fresh.length > 0) {
            if (shownOrderIdRef.current !== fresh[0].id) {
              shownOrderIdRef.current = fresh[0].id;
              setAssignedOrderPopup(fresh[0]);
              setDeclineMode(false);
              setDeclineReasonInput('');
            }
            setPendingAssignments(fresh);
          } else {
            shownOrderIdRef.current = null;
            setAssignedOrderPopup(null);
            setPendingAssignments([]);
          }
        }).catch(() => {});
    };
    check();
    const id = setInterval(check, 15000);
    return () => clearInterval(id);
  }, [currentUser?.id, operatorMachineIds]);

  // Auto-refresh orders every 30s
  useEffect(() => {
    const id = setInterval(loadOrders, 30000);
    return () => clearInterval(id);
  }, [loadOrders]);

  const markOrderResponded = (id) => {
    respondedOrderIdsRef.current.add(id);
    try { localStorage.setItem('op_responded_orders', JSON.stringify([...respondedOrderIdsRef.current])); } catch {}
  };

  // ── Action handler ────────────────────────────────────
  const handleAction = async (action, order) => {
    if (action === 'awaiting_setup' || action === 'drying') return;
    const performerName = userForOp?.full_name || currentUser?.full_name || '';
    setBusyOrderId(order.id);
    try {
      if (action === 'start') {
        const mbStart = order.mixing_batch_info;
        const canStartMix = !mbStart || mbStart.can_start === true || mbStart.status === 'done'
          || Number(mbStart.covered_units) >= 1;
        if (mbStart && !canStartMix) {
          alert('Неможливо почати роботу: замісу ще не вистачає навіть на 1 шт. Додайте матеріали на лінії змішування.');
          setBusyOrderId(null); return;
        }
        const planStart = order.planned_start && new Date(order.planned_start);
        if (planStart && new Date() > planStart) {
          setStartLateTarget(order); setStartLateReason(''); setStartLateOpen(true);
          setBusyOrderId(null); return;
        }
        try {
          await productionOrderService.start(order.id, { performed_by: performerName });
          loadOrders();
        } catch (e) {
          const data = e.response?.data;
          if (apiFlag(data?.packaging_warning) && apiFlag(data?.can_force)) {
            setPackagingWarn({
              order,
              performerName,
              stage: 'start',
              startPayload: { performed_by: performerName },
              detail: apiErrorText(data),
              items: data.packaging || [],
            });
          } else if (apiFlag(data?.co_products_warning) && apiFlag(data?.can_force)) {
            setCoProductsWarn({
              order,
              performerName,
              detail: apiErrorText(data),
              names: data.unordered_products || [],
              startPayload: { performed_by: performerName },
            });
          } else {
            alert(apiErrorText(data, e.message || 'Помилка'));
          }
        }
        setBusyOrderId(null); return;
      } else if (action === 'request_setup') {
        await productionOrderService.requestSetup(order.id, { performed_by: performerName });
        loadOrders();
      } else if (action === 'launch') {
        const sr = order.setup_request;
        if (sr && sr.status !== 'completed') {
          alert('Наладку ще не завершено. Зачекайте, поки наладчик зафіксує наладку.');
          setBusyOrderId(null); return;
        }
        const pendingProcs = (order.additional_processes || []).filter(p => p.operator_status === 'pending');
        if (pendingProcs.length > 0) {
          setProcessDeclarationOrder(order);
          setProcessDeclarations({});
          setProcessDeclarationOpen(true);
          setBusyOrderId(null); return;
        }
        try {
          await productionOrderService.launch(order.id, { performed_by: performerName });
          loadOrders();
        } catch (e) {
          const data = e.response?.data;
          if (data?.can_force) {
            setPackagingWarn({
              order,
              performerName,
              stage: 'launch',
              detail: data.detail,
              items: data.packaging || [],
            });
          } else {
            alert(data?.detail || e.message || 'Помилка');
          }
        }
        setBusyOrderId(null); return;
      } else if (action === 'complete') {
        const planEnd = order.planned_end && new Date(order.planned_end);
        setEndLateTarget(order); setEndLateReason('');
        setCrushingWasteKg('0'); setNonRecoverableWasteKg('');
        setCompleteOrderLate(!!(planEnd && new Date() > planEnd));
        setEndLateOpen(true);
        setBusyOrderId(null); return;
      }
    } catch (e) { alert(e.response?.data?.detail || e.message || 'Помилка'); }
    setBusyOrderId(null);
  };

  const handleSetupSubmit = async () => {
    setSetupSubmitting(true);
    try {
      const wastes = setupWastes.filter((w) => w.quantity > 0).map((w) => ({ raw_material: w.raw_material, quantity: w.quantity }));
      await productionOrderService.setup(setupTarget.id, { raw_material_wastes: wastes });
      setSetupOpen(false); loadOrders();
    } catch (e) { alert(e.response?.data?.detail || e.message || 'Помилка'); }
    setSetupSubmitting(false);
  };

  const handleEndLateSubmit = async () => {
    if (completeOrderLate && !endLateReason.trim()) { alert('Вкажіть причину запізнення'); return; }
    const wasteKg = parseFloat(String(crushingWasteKg).replace(',', '.'));
    if (Number.isNaN(wasteKg) || wasteKg < 0) { alert('Вкажіть кількість відходів (кг), можна 0'); return; }
    setEndLateSubmitting(true);
    try {
      const payload = { crushing_waste_kg: wasteKg };
      if (completeOrderLate) payload.end_late_reason = endLateReason;
      const nonRec = parseFloat(String(nonRecoverableWasteKg).replace(',', '.'));
      if (nonRecoverableWasteKg !== '' && !Number.isNaN(nonRec) && nonRec >= 0) payload.non_recoverable_waste_kg = nonRec;
      await productionOrderService.complete(endLateTarget.id, payload);
      setEndLateOpen(false); loadOrders();
    } catch (e) { alert(e.response?.data?.detail || e.message || 'Помилка'); }
    setEndLateSubmitting(false);
  };

  const handlePauseSubmit = async () => {
    if (!pauseReason) { alert('Оберіть причину зупинки'); return; }
    setPauseSubmitting(true);
    try {
      await productionOrderService.pause(pauseTarget.id, pauseReason);
      setPauseOpen(false); setSelectedOrder(null); setOrderDetailOpen(false); loadOrders();
    } catch (e) { alert(e.response?.data?.detail || e.message || 'Помилка'); }
    setPauseSubmitting(false);
  };

  const handleResume = async (order) => {
    try {
      await productionOrderService.resume(order.id);
      loadOrders();
    } catch (e) { alert(e.response?.data?.detail || e.message || 'Помилка'); }
  };

  const handleMaintStop = async (action, order, ticket) => {
    try {
      if (action === 'stop') {
        await productionOrderService.pause(order.id, 'maintenance');
        if (ticket) {
          await maintenanceTicketService.addComment(ticket.id, {
            text: `Оператор зупинив машину ${order.machine_name || order.machine_identifier || ''} для проведення обслуговування.`,
            author_type: 'maintenance',
            action_label: 'Зупинено оператором',
          });
        }
      } else {
        await productionOrderService.resume(order.id);
        if (ticket) {
          await maintenanceTicketService.addComment(ticket.id, {
            text: `Оператор відновив роботу машини ${order.machine_name || order.machine_identifier || ''}.`,
            author_type: 'maintenance',
            action_label: 'Роботу відновлено',
          });
        }
      }
      loadOrders();
      loadMaintTickets();
    } catch (e) { alert(e.response?.data?.detail || e.message || 'Помилка'); }
  };

  const handleStartLateSubmit = async () => {
    if (!String(startLateReason || '').trim()) {
      alert('Вкажіть причину запізнення старту');
      return;
    }
    setStartLateSubmitting(true);
    try {
      const performerName = userForOp?.full_name || currentUser?.full_name || '';
      const startPayload = { start_late_reason: startLateReason.trim(), performed_by: performerName };
      await productionOrderService.start(startLateTarget.id, startPayload);
      setStartLateOpen(false); loadOrders();
    } catch (e) {
      const data = e.response?.data;
      const performerName = userForOp?.full_name || currentUser?.full_name || '';
      const startPayload = { start_late_reason: startLateReason.trim(), performed_by: performerName };
      if (apiFlag(data?.packaging_warning) && apiFlag(data?.can_force)) {
        setStartLateOpen(false);
        setPackagingWarn({
          order: startLateTarget,
          performerName,
          stage: 'start',
          startPayload,
          detail: apiErrorText(data),
          items: data.packaging || [],
        });
      } else if (apiFlag(data?.co_products_warning) && apiFlag(data?.can_force)) {
        setStartLateOpen(false);
        setCoProductsWarn({
          order: startLateTarget,
          performerName,
          detail: apiErrorText(data),
          names: data.unordered_products || [],
          startPayload,
        });
      } else {
        alert(apiErrorText(data, e.message || 'Помилка'));
      }
    }
    setStartLateSubmitting(false);
  };

  const handleOperatorAccept = async () => {
    if (!assignedOrderPopup) return;
    setPopupSubmitting(true);
    try {
      await productionOrderService.operatorResponse(assignedOrderPopup.id, 'accepted');
      markOrderResponded(assignedOrderPopup.id);
      const remaining = pendingAssignments.filter((o) => o.id !== assignedOrderPopup.id && !respondedOrderIdsRef.current.has(o.id));
      if (remaining.length > 0) {
        shownOrderIdRef.current = remaining[0].id;
        setAssignedOrderPopup(remaining[0]); setDeclineMode(false); setDeclineReasonInput('');
        setPendingAssignments(remaining);
      } else { shownOrderIdRef.current = null; setAssignedOrderPopup(null); setPendingAssignments([]); }
    } catch (e) { alert(e.response?.data?.detail || 'Помилка'); }
    setPopupSubmitting(false);
  };

  const handleOperatorDecline = async () => {
    if (!assignedOrderPopup || !declineReasonInput.trim()) { alert('Вкажіть причину відмови'); return; }
    setPopupSubmitting(true);
    try {
      await productionOrderService.operatorResponse(assignedOrderPopup.id, 'declined', declineReasonInput.trim());
      markOrderResponded(assignedOrderPopup.id);
      const remaining = pendingAssignments.filter((o) => o.id !== assignedOrderPopup.id && !respondedOrderIdsRef.current.has(o.id));
      if (remaining.length > 0) {
        shownOrderIdRef.current = remaining[0].id;
        setAssignedOrderPopup(remaining[0]); setDeclineMode(false); setDeclineReasonInput('');
        setPendingAssignments(remaining);
      } else { shownOrderIdRef.current = null; setAssignedOrderPopup(null); setPendingAssignments([]); }
    } catch (e) { alert(e.response?.data?.detail || 'Помилка'); }
    setPopupSubmitting(false);
  };

  const handleDismissQc = () => {
    if (qcCompleteSubmitting) return;
    setCurrentQcCheck(null);
    setQcCompleteComment('');
    setQcCompletePassed(true);
  };

  const handleOpenQcNow = async () => {
    const list = await loadPendingQc();
    if (list.length === 0) {
      setQcNoPendingOpen(true);
      return;
    }
    setCurrentQcCheck(list[0]);
    setQcCompleteComment('');
    setQcCompletePassed(true);
  };

  const handleCreateQc = async () => {
    if (!operatorMachineIds.length || !currentUser?.id) return;
    setQcNoPendingSubmitting(true);
    try {
      await qualityCheckService.trigger({ machines: operatorMachineIds, operator: currentUser.id, triggered_by: currentUser.id, source: 'operator' });
      setQcNoPendingOpen(false);
      const after = await loadPendingQc();
      if (after.length === 0) {
        alert('Перевірки не зʼявились. Зверніться до адміністратора.');
      } else {
        setCurrentQcCheck(after[0]);
        setQcCompleteComment('');
        setQcCompletePassed(true);
      }
    } catch (e) { alert(e.response?.data?.detail || e.message || 'Помилка'); }
    setQcNoPendingSubmitting(false);
  };

  const handleQcCompleteSubmit = async () => {
    if (!currentQcCheck || !qcCompleteComment.trim()) { alert('Коментар обовʼязковий'); return; }
    setQcCompleteSubmitting(true);
    try {
      await qualityCheckService.complete(currentQcCheck.id, { passed: qcCompletePassed, comment: qcCompleteComment.trim() });
      setCurrentQcCheck(null); setQcCompleteComment(''); setQcCompletePassed(true);
      await loadPendingQc();
    } catch (e) { alert(e.response?.data?.comment?.[0] || e.response?.data?.detail || e.message || 'Помилка'); }
    setQcCompleteSubmitting(false);
  };

  const handleOpenCallQcDialog = async () => {
    setQcMenuAnchor(null);
    try {
      const res = await productionOrderService.getAll({ status: 'in_progress', page_size: 200 });
      const all = res.data?.results || res.data || [];
      const mine = operatorMachineIds.length > 0
        ? all.filter((o) => operatorMachineIds.includes(Number(o.machine)))
        : all;
      setCallQcActiveOrders(mine);
      setCallQcOrderId(mine.length === 1 ? String(mine[0].id) : '');
    } catch { setCallQcActiveOrders([]); }
    setCallQcDialogOpen(true);
  };

  const handleCallQcSubmit = async () => {
    const orderId = callQcOrderId || (callQcActiveOrders.length === 1 ? String(callQcActiveOrders[0].id) : '');
    if (!orderId) { alert('Оберіть замовлення'); return; }
    setCallQcSubmitting(true);
    try {
      await orderQualityCheckService.create({ order: Number(orderId) });
      setCallQcDialogOpen(false);
      setCallQcOrderId('');
      alert('✅ Заявку на контроль якості надіслано відділу КЯ');
    } catch (e) {
      alert(e.response?.data?.detail || e.response?.data?.order?.[0] || e.message || 'Помилка');
    }
    setCallQcSubmitting(false);
  };

  const handleOpenTransfer = async () => {
    setTransferSelected(null);
    setTransferQty('');
    setTransferNote('');
    setTransferError('');
    setTransferOpen(true);
    if (transferStockItems.length === 0) {
      try {
        const res = await api.get('/stock-items/', { params: { page_size: 1000 } });
        const items = res.data?.results || res.data || [];
        setTransferStockItems(items.filter(o => o.quantity > 0));
      } catch { /* ignore */ }
    }
  };

  const handleTransferSubmit = async () => {
    if (!transferSelected) { setTransferError('Оберіть матеріал'); return; }
    const qty = parseFloat(transferQty);
    if (!qty || qty <= 0) { setTransferError('Вкажіть коректну кількість'); return; }
    if (qty > transferSelected.quantity) { setTransferError(`На складі лише ${transferSelected.quantity} ${transferSelected.unit}`); return; }
    setTransferSubmitting(true);
    setTransferError('');
    try {
      await api.post('/production-warehouse/transfer-in/', {
        stock_item: transferSelected.id,
        quantity: qty,
        note: transferNote || `Взято оператором ${currentUser?.full_name || currentUser?.username || ''}`,
      });
      setTransferOpen(false);
      // Оновити список (зменшилась кількість)
      setTransferStockItems([]);
    } catch (e) {
      setTransferError(e.response?.data?.error || 'Помилка при передачі');
    } finally {
      setTransferSubmitting(false);
    }
  };

  const handleOpenShiftReport = () => {
    if (!currentUser?.id) return;
    // Якщо вже подавали цієї сесії — показати екран перегляду/редагування
    if (lastSubmittedReport) {
      setShiftReportAlreadySubmittedOpen(true);
      return;
    }
    openFreshShiftReport();
  };

  const buildShiftReportLines = useCallback((list) => list.map((o) => {
    const prev = lastSubmittedReport?.lines?.find((l) => l.order_id === o.id);
    const kit_part_qtys = buildKitPartQtyState(o, prev);
    return {
      order: o,
      produced_qty: prev && !kit_part_qtys ? String(prev.produced_qty) : (kit_part_qtys
        ? String(Object.values(kit_part_qtys).find((p) => p.is_main)?.produced_qty || '')
        : ''),
      waste_kg: prev ? String(prev.waste_kg) : '',
      non_recoverable_waste_kg: prev ? String(prev.non_recoverable_waste_kg || '') : '',
      defect_qty: '',
      defect_weight_kg: '',
      kit_part_qtys,
      comment: prev?.comment || '',
    };
  }), [lastSubmittedReport]);

  const openFreshShiftReport = () => {
    const filterMine = (list) => (
      operatorMachineIds.length > 0
        ? list.filter((o) => operatorMachineIds.includes(Number(o.machine)))
        : list
    );
    const cached = filterMine(orders.filter((o) => o.status === 'in_progress'));
    // Always open dialog immediately — never wait for network on click
    setShiftReportLines(buildShiftReportLines(cached));
    setShiftReportStep(0);
    setShiftReportDone(false);
    setShiftReportAlreadySubmittedOpen(false);
    setTpaCounters({});
    setTpaCounterHints({});
    setEfficiencyPreview(null);
    setEfficiencyLowReason('');
    setEfficiencyLowComment('');
    setShiftReportLoading(true);
    setShiftReportOpen(true);
    if (efficiencyReasons.length === 0) {
      efficiencyLowReasonService.getAll()
        .then((r) => setEfficiencyReasons(r.data?.results || r.data || []))
        .catch(() => setEfficiencyReasons([]));
    }

    const params = {};
    if (operatorMachineIds.length > 0) {
      params.machines = operatorMachineIds.join(',');
    }
    productionOrderService.shiftReportCandidates(params)
      .then((res) => {
        const mine = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        setShiftReportLines(buildShiftReportLines(mine));
        const mids = [...new Set(mine.map((o) => Number(o.machine)).filter(Boolean))];
        if (mids.length > 0) {
          api.get('/shift-handovers/last-counters/', { params: { machines: mids.join(',') } })
            .then((cr) => {
              const rows = Array.isArray(cr.data) ? cr.data : [];
              const map = {};
              rows.forEach((r) => {
                map[Number(r.machine_id)] = r;
              });
              setTpaCounterHints(map);
            })
            .catch(() => {});
        }
      })
      .catch(() => {
        if (cached.length === 0) {
          alert('Помилка завантаження');
        }
      })
      .finally(() => setShiftReportLoading(false));
  };

  /** Унікальні ТПА зі рядків звіту (для обовʼязкового лічильника). */
  // ТПА, по яких оператор щось ввів: лише для них потрібен лічильник (порожні рядки не зберігаються)
  const shiftReportMachines = useMemo(() => {
    const map = new Map();
    const hasData = (l) => {
      const n = (v) => Number(v) || 0;
      if ([l.produced_qty, l.waste_kg, l.non_recoverable_waste_kg, l.defect_qty, l.defect_weight_kg].some((v) => n(v) > 0)) return true;
      if (Object.values(l.kit_part_qtys || {}).some((p) => n(p.produced_qty) > 0 || n(p.defect_qty) > 0)) return true;
      return Boolean((l.comment || '').trim());
    };
    (shiftReportLines || []).filter(hasData).forEach((l) => {
      const o = l?.order;
      const mid = Number(o?.machine);
      if (!mid || map.has(mid)) return;
      map.set(mid, {
        id: mid,
        identifier: o.machine_identifier || myMachines.find((m) => Number(m.id) === mid)?.identifier || `#${mid}`,
      });
    });
    return [...map.values()];
  }, [shiftReportLines, myMachines]);

  const buildShiftReportPayload = useCallback(() => {
    return shiftReportLines.map((l) => {
      const kitQtys = l.kit_part_qtys || null;
      let producedQty = Number(l.produced_qty) || 0;
      let defectQty = Number(l.defect_qty) || 0;
      let coRows = [];
      const defectWeightKg = Number(l.defect_weight_kg) || 0;
      if (kitQtys) {
        const main = Object.values(kitQtys).find((p) => p.is_main);
        producedQty = Number(main?.produced_qty) || 0;
        if (defectWeightKg > 0) {
          defectQty = 0;
          coRows = Object.values(kitQtys)
            .filter((p) => !p.is_main)
            .map((p) => ({
              co_produced_id: p.co_id,
              product_id: p.product_id,
              produced_qty: Number(p.produced_qty) || 0,
              defect_qty: 0,
            }));
        } else {
          defectQty = Number(main?.defect_qty) || 0;
          coRows = Object.values(kitQtys)
            .filter((p) => !p.is_main)
            .map((p) => ({
              co_produced_id: p.co_id,
              product_id: p.product_id,
              produced_qty: Number(p.produced_qty) || 0,
              defect_qty: Number(p.defect_qty) || 0,
            }));
        }
      }
      return {
        order: l.order.id,
        operator: currentUser.id,
        produced_qty: producedQty,
        waste_kg: Number(l.waste_kg) || 0,
        non_recoverable_waste_kg: Number(l.non_recoverable_waste_kg) || 0,
        defect_qty: defectQty,
        defect_weight_kg: defectWeightKg,
        co_produced_qtys: coRows,
        comment: l.comment || '',
      };
    });
  }, [shiftReportLines, currentUser]);

  const refreshEfficiencyPreview = async () => {
    if (!currentUser?.id || !shiftReportLines.length) return;
    setEfficiencyPreviewLoading(true);
    try {
      const payload = buildShiftReportPayload();
      const res = await operatorShiftReportService.previewEfficiency({ reports: payload });
      setEfficiencyPreview(res.data);
    } catch (e) {
      console.error(e);
      setEfficiencyPreview(null);
    } finally {
      setEfficiencyPreviewLoading(false);
    }
  };

  const handleShiftReportSubmit = async () => {
    // Валідація лічильників перед відправкою
    for (const m of shiftReportMachines) {
      const raw = tpaCounters[m.id];
      if (raw === undefined || raw === null || String(raw).trim() === '') {
        alert(`Вкажіть показник лічильника ТПА для ${m.identifier}`);
        return;
      }
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
        alert(`Показник лічильника для ${m.identifier} має бути цілим числом ≥ 0`);
        return;
      }
      const prev = tpaCounterHints[m.id]?.last_counter_reading;
      if (prev != null && n < Number(prev)) {
        alert(
          `ТПА ${m.identifier}: показник (${n}) менший за попередній кінець зміни (${prev}). Перевірте зняття з лічильника.`
        );
        return;
      }
    }

    let preview = efficiencyPreview;
    try {
      const payload = buildShiftReportPayload();
      const res = await operatorShiftReportService.previewEfficiency({ reports: payload });
      preview = res.data;
      setEfficiencyPreview(preview);
    } catch (_) { /* submit все одно перевірить на бекенді */ }

    if (preview?.requires_low_reason) {
      if (!efficiencyLowReason) {
        alert('Ефективність нижче 100% — оберіть причину з довідника');
        return;
      }
      const reason = efficiencyReasons.find((r) => String(r.id) === String(efficiencyLowReason));
      if (reason?.comment_required && !efficiencyLowComment.trim()) {
        alert(`Для причини «${reason.code}» коментар обовʼязковий`);
        return;
      }
    }

    setShiftReportSubmitting(true);
    try {
      const payload = buildShiftReportPayload();
      const counters = shiftReportMachines.map((m) => ({
        machine: m.id,
        counter_reading: Number(tpaCounters[m.id]),
      }));
      await operatorShiftReportService.submitWithCounters({
        reports: payload,
        counters,
        low_reason: efficiencyLowReason || undefined,
        low_comment: efficiencyLowComment || '',
      });
      // Зберігаємо в sessionStorage для показу при повторному відкритті
      const saved = {
        submitted_at: new Date().toISOString(),
        lines: shiftReportLines.map((l) => {
          const kit = getOrderKitParts(l.order);
          const kitQtys = l.kit_part_qtys;
          const mainProduced = kitQtys
            ? Number(Object.values(kitQtys).find((p) => p.is_main)?.produced_qty) || 0
            : Number(l.produced_qty) || 0;
          return {
            order_id: l.order.id,
            order_name: formatOrderKitDescription(l.order),
            kit_parts: kit.isKit
              ? kit.parts.map((p) => ({
                name: p.name,
                qty: p.qty,
                produced_qty: Number(kitQtys?.[p.key]?.produced_qty) || 0,
                defect_qty: Number(kitQtys?.[p.key]?.defect_qty) || 0,
              }))
              : null,
            kit_part_qtys: kitQtys || null,
            machine: l.order.machine_identifier,
            customer_order_number: l.order.customer_order_number,
            produced_qty: mainProduced,
            waste_kg: Number(l.waste_kg) || 0,
            non_recoverable_waste_kg: Number(l.non_recoverable_waste_kg) || 0,
            comment: l.comment || '',
          };
        }),
      };
      sessionStorage.setItem(shiftReportKey, JSON.stringify(saved));
      setLastSubmittedReport(saved);
      setShiftReportDone(true);
      loadOrders();
    } catch (e) {
      const d = e.response?.data;
      const msg = d?.detail
        || d?.counter_reading
        || (typeof d === 'string' ? d : null)
        || 'Помилка збереження звіту';
      alert(Array.isArray(msg) ? msg.join(' ') : msg);
    }
    setShiftReportSubmitting(false);
  };

  // ─── RENDER ──────────────────────────────────────────
  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>

      {/* ── Sticky Header ───────────────────────────────── */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 200,
        bgcolor: 'white',
        px: 2, pt: 1, pb: isMobile ? 0.75 : 1,
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between', gap: isMobile ? 0.75 : 0,
        borderBottom: '1px solid #e0e0e0',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      }}>
        {/* Перемикач ТПА / Замовлення */}
        <Box sx={{ display: 'flex', bgcolor: '#f0f0f0', borderRadius: 1.5, p: 0.4, gap: 0.5 }}>
          <Button size="small" onClick={() => setMainView('tpa')}
            startIcon={<PrecisionManufacturingIcon sx={{ fontSize: 15 }} />}
            sx={{ flex: isMobile ? 1 : 'unset',
              textTransform: 'none', fontWeight: 700, fontSize: '0.85rem', px: 2, py: 0.6, minWidth: 0,
              bgcolor: mainView === 'tpa' ? '#1976d2' : 'transparent',
              color: mainView === 'tpa' ? 'white' : '#666', borderRadius: 1,
              boxShadow: mainView === 'tpa' ? '0 1px 4px rgba(25,118,210,0.3)' : 'none',
              '&:hover': { bgcolor: mainView === 'tpa' ? '#1565c0' : '#e4e4e4' } }}>
            ТПА ({myMachines.length})
          </Button>
          <Button size="small" onClick={() => setMainView('orders')}
            sx={{ flex: isMobile ? 1 : 'unset',
              textTransform: 'none', fontWeight: 700, fontSize: '0.85rem', px: 2, py: 0.6, minWidth: 0,
              bgcolor: mainView === 'orders' ? '#1976d2' : 'transparent',
              color: mainView === 'orders' ? 'white' : '#666', borderRadius: 1,
              boxShadow: mainView === 'orders' ? '0 1px 4px rgba(25,118,210,0.3)' : 'none',
              '&:hover': { bgcolor: mainView === 'orders' ? '#1565c0' : '#e4e4e4' } }}>
            Замовлення
          </Button>
        </Box>

        {/* Кнопки дій */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1,
          justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
          <Button variant="outlined" size="small"
            startIcon={<PrecisionManufacturingIcon sx={{ fontSize: 15 }} />}
            disabled
            sx={{ flex: isMobile ? 1 : 'unset',
              textTransform: 'none', fontSize: '0.8rem', fontWeight: 600,
              borderColor: '#1976d2', color: '#1976d2' }}>
            {myMachines.length} ТПА
          </Button>
          <Badge badgeContent={pendingQcChecks.length} color="error"
            sx={{ flex: isMobile ? 1 : 'unset' }}>
            <Button variant="outlined" size="small" fullWidth={isMobile}
              startIcon={<VerifiedUserIcon sx={{ fontSize: 15 }} />}
              endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => setQcMenuAnchor(e.currentTarget)}
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
                borderColor: '#1976d2', color: '#1976d2',
                '&:hover': { bgcolor: '#e3f2fd' } }}>
              {isMobile ? 'КЯ' : 'Контроль якості'}{pendingQcChecks.length > 0 ? ` (${pendingQcChecks.length})` : ''}
            </Button>
          </Badge>
          <Menu anchorEl={qcMenuAnchor} open={Boolean(qcMenuAnchor)} onClose={() => setQcMenuAnchor(null)}
            PaperProps={{ sx: { borderRadius: 2, minWidth: 240, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } }}>
            <MenuItem onClick={() => { setQcMenuAnchor(null); handleOpenQcNow(); }}
              sx={{ py: 1.2 }}>
              <ListItemIcon><AssignmentTurnedInIcon sx={{ color: '#1976d2' }} /></ListItemIcon>
              <ListItemText
                primary="Провести контроль якості"
                secondary="Оператор виконує перевірку"
                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleOpenCallQcDialog} sx={{ py: 1.2 }}>
              <ListItemIcon><CallIcon sx={{ color: '#e65100' }} /></ListItemIcon>
              <ListItemText
                primary="Викликати контроль якості"
                secondary="Надіслати заявку відділу КЯ"
                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', color: '#e65100' }}
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
            </MenuItem>
          </Menu>
          <Button variant="outlined" size="small"
            startIcon={<MoveToInboxIcon sx={{ fontSize: 15 }} />}
            onClick={handleOpenTransfer}
            sx={{ flex: isMobile ? 1 : 'unset',
              textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
              borderColor: '#7b1fa2', color: '#7b1fa2',
              '&:hover': { bgcolor: '#f3e5f5' } }}>
            {isMobile ? 'Склад' : 'Взяти зі складу'}
          </Button>
          <Button variant="contained" size="small"
            startIcon={<AssessmentIcon sx={{ fontSize: 15 }} />}
            onClick={handleOpenShiftReport}
            sx={{ flex: isMobile ? 1 : 'unset',
              bgcolor: '#43a047', textTransform: 'none', fontWeight: 700, fontSize: '0.8rem',
              px: isMobile ? 1 : 2,
              '&:hover': { bgcolor: '#388e3c' } }}>
            {isMobile ? 'Звіт' : 'Звіт за зміну'}
          </Button>
          <Button variant="outlined" size="small"
            startIcon={<SwapHorizIcon sx={{ fontSize: 15 }} />}
            onClick={() => setHandoverDialogOpen(true)}
            sx={{ flex: isMobile ? 1 : 'unset',
              textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
              borderColor: '#0288d1', color: '#0288d1',
              '&:hover': { bgcolor: '#e1f5fe' } }}>
            {isMobile ? 'Зміна' : 'Передача зміни'}
          </Button>
        </Box>
      </Box>

      {/* ── Main content ───────────────────────────────── */}
      <Box sx={{ p: 2.5, maxWidth: 1400, mx: 'auto' }}>

        {/* ─── TPA view ──────────────────────────────────── */}
        {/* ─── TPA view ──────────────────────────────────── */}
        {mainView === 'tpa' && (
          <Box>
            {loading || assignedMachineIds === null ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : myMachines.length === 0 ? (
              <Paper sx={{ borderRadius: 3, p: 3, textAlign: 'center' }}>
                <PrecisionManufacturingIcon sx={{ fontSize: 40, color: '#bbb', mb: 1 }} />
                <Typography color="text.secondary" variant="body2" fontWeight={600}>Вам не призначено ТПА на сьогодні</Typography>
                <Typography color="text.secondary" variant="caption">Зверніться до керівника для призначення зміни</Typography>
              </Paper>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                {myMachines.map((m) => (
                  <TpaTile
                    key={m.id}
                    machine={m}
                    orders={ordersByMachine[m.id] || []}
                    onClick={(machine, ords) => { setTpaDialogMachine(machine); setTpaDialogOrders(ords); setTpaDialogOpen(true); }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* ─── Orders view ───────────────────────────────── */}
        {mainView === 'orders' && <Box>
          {/* Filter chips */}
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
              {ORDER_FILTERS.map((f) => {
                const count = (() => {
                  if (f.value === 'all') return orders.filter((o) => operatorMachineIds.length === 0 || operatorMachineIds.includes(Number(o.machine))).length;
                  if (f.value === 'active') return orders.filter((o) => (operatorMachineIds.length === 0 || operatorMachineIds.includes(Number(o.machine))) && ['planned','in_progress','paused'].includes(o.status)).length;
                  return orders.filter((o) => (operatorMachineIds.length === 0 || operatorMachineIds.includes(Number(o.machine))) && o.status === f.value).length;
                })();
                return (
                  <Chip
                    key={f.value}
                    label={`${f.label}${count > 0 ? ` (${count})` : ''}`}
                    onClick={() => setOrderFilter(f.value)}
                    sx={{
                      cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem',
                      bgcolor: orderFilter === f.value ? '#1976d2' : 'white',
                      color: orderFilter === f.value ? 'white' : '#555',
                      border: '1px solid',
                      borderColor: orderFilter === f.value ? '#1976d2' : '#ddd',
                      '&:hover': { bgcolor: orderFilter === f.value ? '#1565c0' : '#f5f5f5' },
                    }}
                  />
                );
              })}
          </Box>

          {/* Order tiles grid */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : filteredOrders.length === 0 ? (
            <Paper sx={{ borderRadius: 3, p: 4, textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 48, color: '#a5d6a7', mb: 1 }} />
              <Typography color="text.secondary">Немає замовлень у цій категорії</Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(2, 1fr)' }, gap: 2 }}>
              {filteredOrders.map((o) => (
                <OrderTile
                  key={o.id}
                  order={o}
                  busy={busyOrderId === o.id}
                  onClick={(order) => { setSelectedOrder(order); setOrderDetailOpen(true); }}
                  onAction={handleAction}
                  onRedoSetup={(order) => setRedoSetupDialog(order)}
                  maintTicket={maintTickets.find((t) => t.production_order === o.id) || null}
                  onMaintStop={handleMaintStop}
                />
              ))}
            </Box>
          )}
        </Box>}
      </Box>

      {/* ═══════════ DIALOGS ═══════════════════════════════ */}

      {/* Order Detail */}
      <OrderDetailDialog
        open={orderDetailOpen}
        order={selectedOrder}
        onClose={() => setOrderDetailOpen(false)}
        dryingRows={dryingRows}
        onAction={handleAction}
        onPause={(order) => { setPauseTarget(order); setPauseReason(''); setPauseOpen(true); }}
        onResume={handleResume}
        busy={selectedOrder && busyOrderId === selectedOrder.id}
        onRedoSetup={(order) => setRedoSetupDialog(order)}
        maintTicket={selectedOrder ? maintTickets.find((t) => t.production_order === selectedOrder.id) || null : null}
        onMaintStop={handleMaintStop}
      />

      {/* Pause dialog */}
      <Dialog open={pauseOpen} onClose={() => !pauseSubmitting && setPauseOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ bgcolor: '#e65100', color: 'white', px: 3, py: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>⏸ Зупинити замовлення</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>{pauseTarget?.product_name}</Typography>
        </Box>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Оберіть причину зупинки:</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {PAUSE_REASONS.map((r) => (
              <Button
                key={r.value}
                variant={pauseReason === r.value ? 'contained' : 'outlined'}
                onClick={() => setPauseReason(r.value)}
                sx={{
                  justifyContent: 'flex-start', textTransform: 'none', fontWeight: 600, borderRadius: 2,
                  bgcolor: pauseReason === r.value ? '#e65100' : 'transparent',
                  borderColor: pauseReason === r.value ? '#e65100' : '#ffccbc',
                  color: pauseReason === r.value ? 'white' : '#e65100',
                  '&:hover': { bgcolor: pauseReason === r.value ? '#bf360c' : '#fff3e0', borderColor: '#e65100' },
                }}
              >
                {r.label}
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button fullWidth variant="contained" disabled={!pauseReason || pauseSubmitting} onClick={handlePauseSubmit}
            sx={{ bgcolor: '#e65100', borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#bf360c' } }}>
            {pauseSubmitting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Зупинити'}
          </Button>
          <Button fullWidth variant="outlined" onClick={() => setPauseOpen(false)} sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none' }}>
            Скасувати
          </Button>
        </DialogActions>
      </Dialog>

      {/* TPA Queue */}
      <TpaQueueDialog
        open={tpaDialogOpen}
        machine={tpaDialogMachine}
        orders={tpaDialogOrders}
        onClose={() => setTpaDialogOpen(false)}
        onAction={handleAction}
        busyOrderId={busyOrderId}
      />

      {/* Assignment popup */}
      <Dialog open={Boolean(assignedOrderPopup)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ bgcolor: '#1565c0', color: 'white', px: 3, py: 2 }}>
          <Typography variant="overline" sx={{ opacity: 0.8 }}>
            Нове призначення {pendingAssignments.length > 1 && `(${pendingAssignments.findIndex((o) => o.id === assignedOrderPopup?.id) + 1}/${pendingAssignments.length})`}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{assignedOrderPopup?.product_name}</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            {assignedOrderPopup?.machine_identifier} · {assignedOrderPopup?.quantity} шт
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {fmtDt(assignedOrderPopup?.planned_start)} → {fmtDt(assignedOrderPopup?.planned_end)}
            </Typography>
          </Box>
          {!declineMode ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
              <Button fullWidth variant="contained" size="large"
                startIcon={popupSubmitting ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <CheckCircleIcon />}
                disabled={popupSubmitting} onClick={handleOperatorAccept}
                sx={{ bgcolor: '#2e7d32', borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none' }}>
                Беру в роботу
              </Button>
              <Button fullWidth variant="outlined" color="error" size="large"
                onClick={() => setDeclineMode(true)}
                sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none' }}>
                Не можу виконати
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
              <TextField label="Причина відмови" fullWidth multiline rows={2} value={declineReasonInput} onChange={(e) => setDeclineReasonInput(e.target.value)} />
              <Button fullWidth variant="contained" color="error" size="large"
                startIcon={popupSubmitting ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <CloseIcon />}
                disabled={popupSubmitting} onClick={handleOperatorDecline}
                sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none' }}>
                Підтвердити відмову
              </Button>
              <Button fullWidth variant="text" onClick={() => setDeclineMode(false)} sx={{ textTransform: 'none' }}>Назад</Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* QC Complete — відкривається лише з меню «Контроль якості», не блокує роботу */}
      <Dialog
        open={Boolean(currentQcCheck)}
        onClose={handleDismissQc}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <Box sx={{ bgcolor: '#4a148c', color: 'white', px: 3, py: 2, pr: 1, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="overline" sx={{ opacity: 0.8 }}>Контроль якості</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {currentQcCheck?.machine_identifier || `ТПА #${currentQcCheck?.machine}`}
            </Typography>
          </Box>
          <IconButton
            aria-label="Закрити"
            onClick={handleDismissQc}
            disabled={qcCompleteSubmitting}
            sx={{ color: 'white', mt: -0.5 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {currentQcCheck?.order_product_name || currentQcCheck?.description || 'Перевірка продукції'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            <Button fullWidth variant={qcCompletePassed ? 'contained' : 'outlined'} size="large"
              onClick={() => setQcCompletePassed(true)}
              sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none', ...(qcCompletePassed ? { bgcolor: '#2e7d32' } : { borderColor: '#2e7d32', color: '#2e7d32' }) }}>
              ✓ Пройшло
            </Button>
            <Button fullWidth variant={!qcCompletePassed ? 'contained' : 'outlined'} size="large"
              onClick={() => setQcCompletePassed(false)}
              sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none', ...(!qcCompletePassed ? { bgcolor: '#c62828' } : { borderColor: '#c62828', color: '#c62828' }) }}>
              ✗ Не пройшло
            </Button>
          </Box>
          <TextField label="Коментар (обовʼязковий)" fullWidth multiline rows={3} value={qcCompleteComment} onChange={(e) => setQcCompleteComment(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexDirection: 'column' }}>
          <Button fullWidth variant="contained" size="large"
            startIcon={qcCompleteSubmitting ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <CheckCircleIcon />}
            disabled={qcCompleteSubmitting} onClick={handleQcCompleteSubmit}
            sx={{ bgcolor: '#4a148c', borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none' }}>
            Зберегти результат
          </Button>
          <Button fullWidth variant="text" disabled={qcCompleteSubmitting} onClick={handleDismissQc} sx={{ textTransform: 'none' }}>
            Пізніше
          </Button>
        </DialogActions>
      </Dialog>

      {/* No QC */}
      <Dialog open={qcNoPendingOpen} onClose={() => setQcNoPendingOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>Немає відкритих перевірок</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">Можна створити перевірку вручну для ваших ТПА.</Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 2, pb: 2 }}>
          <Button fullWidth variant="contained" size="large"
            startIcon={qcNoPendingSubmitting ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <VerifiedUserIcon />}
            disabled={qcNoPendingSubmitting} onClick={handleCreateQc}
            sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none' }}>
            Створити перевірку
          </Button>
          <Button fullWidth variant="text" onClick={() => setQcNoPendingOpen(false)} sx={{ textTransform: 'none' }}>Закрити</Button>
        </DialogActions>
      </Dialog>

      {/* ── Call QC dialog ──────────────────────────────────────────────────── */}
      <Dialog open={callQcDialogOpen} onClose={() => !callQcSubmitting && setCallQcDialogOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ bgcolor: '#e65100', color: 'white', px: 3, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CallIcon sx={{ fontSize: 26 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>Викликати контроль якості</Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>Заявка надійде до відділу КЯ</Typography>
            </Box>
          </Box>
        </Box>
        <DialogContent sx={{ pt: 2.5 }}>
          {callQcActiveOrders.length === 0 ? (
            <Typography color="text.secondary" variant="body2">Немає активних замовлень для ваших ТПА.</Typography>
          ) : callQcActiveOrders.length === 1 ? (
            <Box sx={{ p: 1.5, bgcolor: '#fff3e0', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">Замовлення</Typography>
              <Typography variant="subtitle1" fontWeight={700}>{callQcActiveOrders[0].product_name}</Typography>
              <Typography variant="body2" color="text.secondary">ТПА: {callQcActiveOrders[0].machine_identifier || callQcActiveOrders[0].machine_name}</Typography>
            </Box>
          ) : (
            <TextField select fullWidth label="Оберіть замовлення" size="small"
              value={callQcOrderId}
              onChange={(e) => setCallQcOrderId(e.target.value)}
              SelectProps={{ native: false }}
            >
              {callQcActiveOrders.map((o) => (
                <MenuItem key={o.id} value={String(o.id)}>
                  {o.product_name} — {o.machine_identifier || o.machine_name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button onClick={() => setCallQcDialogOpen(false)} disabled={callQcSubmitting} sx={{ textTransform: 'none' }}>Скасувати</Button>
          <Button variant="contained" disabled={callQcSubmitting || (!callQcOrderId && callQcActiveOrders.length !== 1)}
            onClick={handleCallQcSubmit}
            startIcon={callQcSubmitting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <CallIcon />}
            sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' }, textTransform: 'none', fontWeight: 700 }}>
            Надіслати заявку
          </Button>
        </DialogActions>
      </Dialog>

      {/* Setup wastes */}
      <Dialog open={setupOpen} onClose={() => setSetupOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ bgcolor: '#e65100', color: 'white', px: 3, py: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Наладка — відходи на запуск</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>{setupTarget?.product_name}</Typography>
        </Box>
        <DialogContent sx={{ pt: 2 }}>
          {setupWastes.length === 0 ? (
            <Typography color="text.secondary" variant="body2">Матеріали не задано — підтвердіть без відходів</Typography>
          ) : setupWastes.map((w, i) => (
            <Box key={w.raw_material} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="body2" sx={{ flex: 1 }}>{w.raw_material_name}</Typography>
              <TextField label="Відходи (кг)" type="number" size="small"
                value={w.quantity}
                onChange={(e) => { const u = [...setupWastes]; u[i] = { ...w, quantity: e.target.value }; setSetupWastes(u); }}
                sx={{ width: 130 }} inputProps={{ min: 0, step: 0.001 }} />
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSetupOpen(false)} sx={{ textTransform: 'none' }}>Скасувати</Button>
          <Button variant="contained" size="large"
            startIcon={setupSubmitting ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <BuildIcon />}
            disabled={setupSubmitting} onClick={handleSetupSubmit}
            sx={{ bgcolor: '#e65100', borderRadius: 2, px: 3, fontWeight: 700, textTransform: 'none' }}>
            Підтвердити наладку
          </Button>
        </DialogActions>
      </Dialog>

      {/* Packaging shortage soft-block dialog */}
      <Dialog open={!!packagingWarn} onClose={() => setPackagingWarn(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box sx={{ bgcolor: '#e65100', px: 3, py: 2 }}>
          <Typography variant="h6" fontWeight={800} color="#fff">Нестача тари</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.25 }}>
            На складі менше, ніж потрібно на весь тираж
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600 }}>
            {packagingWarn?.detail
              || 'Увага! На складі тари менше, ніж потрібно. Запустити виробництво на наявний обсяг?'}
          </Typography>
          {(packagingWarn?.items || []).map((item, i) => {
            const label = typeof item === 'string'
              ? item
              : (item.label || `«${item.name}»: є ${item.available}, потрібно ${item.needed}`);
            return (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1,
                bgcolor: '#fff3e0', borderRadius: 1.5, p: 1.25 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#e65100', mt: 0.75, flexShrink: 0 }} />
                <Typography variant="body2">{label}</Typography>
              </Box>
            );
          })}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Наявний обсяг тари буде списано; на нестачу створиться заявка на переміщення.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setPackagingWarn(null)} variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, flex: 1 }}>
            Скасувати
          </Button>
          <Button variant="contained" onClick={async () => {
            const { order, performerName, startPayload, stage } = packagingWarn;
            setPackagingWarn(null);
            setBusyOrderId(order.id);
            try {
              if (stage === 'start') {
                await productionOrderService.start(order.id, {
                  ...(startPayload || {}),
                  performed_by: performerName,
                  force_packaging: true,
                });
              } else {
                await productionOrderService.launch(order.id, {
                  performed_by: performerName,
                  force: true,
                  force_packaging: true,
                });
              }
              loadOrders();
            } catch (e) { alert(apiErrorText(e.response?.data, e.message || 'Помилка')); }
            setBusyOrderId(null);
          }} sx={{ bgcolor: '#e65100', borderRadius: 2, textTransform: 'none', fontWeight: 700, flex: 1,
            '&:hover': { bgcolor: '#bf360c' } }}>
            Так, запустити
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unordered co-products warning on start */}
      <Dialog open={!!coProductsWarn} onClose={() => setCoProductsWarn(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box sx={{ bgcolor: '#6a1b9a', px: 3, py: 2 }}>
          <Typography variant="h6" fontWeight={800} color="#fff">Супутні вироби комплекту</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.25 }}>
            Незамовлені позиції підуть на склад виробництва
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {coProductsWarn?.detail || 'Паралельно будуть виготовлені незамовлені супутні вироби. Сировина розрахована на повний цикл.'}
          </Typography>
          {(coProductsWarn?.names || []).map((name, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1,
              bgcolor: '#f3e5f5', borderRadius: 1.5, p: 1.25 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#6a1b9a', mt: 0.75, flexShrink: 0 }} />
              <Typography variant="body2">{name}</Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setCoProductsWarn(null)} variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, flex: 1 }}>
            Скасувати
          </Button>
          <Button variant="contained" onClick={async () => {
            const { order, startPayload, performerName } = coProductsWarn;
            setCoProductsWarn(null);
            setBusyOrderId(order.id);
            try {
              await productionOrderService.start(order.id, { ...(startPayload || {}), force: true });
              loadOrders();
            } catch (e) {
              const data = e.response?.data;
              if (apiFlag(data?.packaging_warning) && apiFlag(data?.can_force)) {
                setPackagingWarn({
                  order,
                  performerName,
                  stage: 'start',
                  startPayload: { ...(startPayload || {}), force: true },
                  detail: apiErrorText(data),
                  items: data.packaging || [],
                });
              } else {
                alert(apiErrorText(data, e.message || 'Помилка'));
              }
            }
            setBusyOrderId(null);
          }} sx={{ bgcolor: '#6a1b9a', borderRadius: 2, textTransform: 'none', fontWeight: 700, flex: 1,
            '&:hover': { bgcolor: '#4a148c' } }}>
            Почати все одно
          </Button>
        </DialogActions>
      </Dialog>

      {/* Redo setup dialog */}
      <Dialog open={!!redoSetupDialog} onClose={() => setRedoSetupDialog(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box sx={{ bgcolor: '#37474f', px: 3, py: 2 }}>
          <Typography variant="h6" fontWeight={800} color="#fff">Повторна наладка</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            {redoSetupDialog?.product_name} · {redoSetupDialog?.machine_identifier}
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Буде створено нову заявку на наладку. Наладчик повинен буде виконати наладку перед запуском виробництва.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Причина: відновлення після паузи / позапланова перерва.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setRedoSetupDialog(null)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', flex: 1 }}>
            Скасувати
          </Button>
          <Button variant="contained" onClick={async () => {
            const order = redoSetupDialog;
            setRedoSetupDialog(null);
            try {
              const performer = currentUser?.full_name || '';
              await setupRequestService.redo({ order: order.id, performed_by: performer, reason: 'Повторна наладка після паузи' });
              loadOrders();
            } catch (e) { alert(e.response?.data?.detail || e.message || 'Помилка'); }
          }} sx={{ bgcolor: '#37474f', borderRadius: 2, textTransform: 'none', fontWeight: 700, flex: 1,
            '&:hover': { bgcolor: '#263238' } }}>
            Призначити наладку
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete dialog */}
      <Dialog open={endLateOpen} onClose={() => setEndLateOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ bgcolor: '#2e7d32', color: 'white', px: 3, py: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Завершення замовлення</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>{endLateTarget?.product_name}</Typography>
        </Box>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Відходи для дроблення (кг)" type="number" fullWidth
            value={crushingWasteKg} onChange={(e) => setCrushingWasteKg(e.target.value)}
            inputProps={{ min: 0, step: 0.001 }} helperText="Вкажіть 0 якщо відходів немає" />
          <TextField label="Невідновлювані відходи (кг, необовʼязково)" type="number" fullWidth
            value={nonRecoverableWasteKg} onChange={(e) => setNonRecoverableWasteKg(e.target.value)}
            inputProps={{ min: 0, step: 0.001 }} />
          {completeOrderLate && (
            <TextField label="Причина запізнення (обовʼязково)" fullWidth multiline rows={2}
              value={endLateReason} onChange={(e) => setEndLateReason(e.target.value)} />
          )}
          {(endLateTarget?.co_produced || []).length > 0 && (() => {
            const { parts } = getOrderKitParts(endLateTarget);
            const cosByName = Object.fromEntries(
              (endLateTarget.co_produced || []).map((c) => [c.product_name, c])
            );
            return (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Комплект (буде зафіксовано):</Typography>
                {parts.map((p) => {
                  const co = cosByName[p.name];
                  const dest = !co
                    ? (endLateTarget.customer_order_number ? `Замовлення #${endLateTarget.customer_order_number}` : 'основна')
                    : (co.customer_order_number ? `Замовлення #${co.customer_order_number}` : 'Склад виробництва');
                  return (
                    <Box key={p.key} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.3, gap: 1 }}>
                      <Typography variant="body2">{p.name}</Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                        {p.qty.toLocaleString('uk-UA')} шт → {dest}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setEndLateOpen(false)} sx={{ textTransform: 'none' }}>Скасувати</Button>
          <Button variant="contained" size="large"
            startIcon={endLateSubmitting ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <CheckCircleIcon />}
            disabled={endLateSubmitting} onClick={handleEndLateSubmit}
            sx={{ bgcolor: '#2e7d32', borderRadius: 2, px: 3, fontWeight: 700, textTransform: 'none' }}>
            Завершити
          </Button>
        </DialogActions>
      </Dialog>

      {/* Start late */}
      <Dialog open={startLateOpen} onClose={() => setStartLateOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Запізнення початку</DialogTitle>
        <DialogContent>
          <TextField label="Причина запізнення" fullWidth multiline rows={2}
            value={startLateReason} onChange={(e) => setStartLateReason(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setStartLateOpen(false)} sx={{ textTransform: 'none' }}>Скасувати</Button>
          <Button variant="contained" size="large"
            startIcon={startLateSubmitting ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <PlayArrowIcon />}
            disabled={startLateSubmitting || !String(startLateReason || '').trim()}
            onClick={handleStartLateSubmit}
            sx={{ bgcolor: '#1565c0', borderRadius: 2, px: 3, fontWeight: 700, textTransform: 'none' }}>
            Почати роботу
          </Button>
        </DialogActions>
      </Dialog>

      {/* Already submitted dialog */}
      <Dialog open={shiftReportAlreadySubmittedOpen} onClose={() => setShiftReportAlreadySubmittedOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ bgcolor: '#f57f17', color: 'white', px: 3, py: 2 }}>
          <Typography variant="overline" sx={{ opacity: 0.85 }}>Звіт за зміну</Typography>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Звіт вже подано цієї сесії</Typography>
          {lastSubmittedReport?.submitted_at && (
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Подано о {new Date(lastSubmittedReport.submitted_at).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          )}
        </Box>
        <DialogContent sx={{ pt: 2 }}>
          {(lastSubmittedReport?.lines || []).map((l, i) => (
            <Paper key={i} variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                <Box>
                  {l.customer_order_number && (
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#5c6bc0' }}>
                      № {l.customer_order_number}
                    </Typography>
                  )}
                  {(l.kit_parts || []).length > 0 ? (
                    (l.kit_parts || []).map((p, pi) => (
                      <Box key={pi} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{p.name}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {Number(p.qty).toLocaleString('uk-UA')} шт
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{l.order_name}</Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">{l.machine}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                {(l.kit_parts || []).length > 0 ? (
                  (l.kit_parts || []).map((p, pi) => (
                    <Box key={pi} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">{p.name}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {Number(p.produced_qty || 0).toLocaleString('uk-UA')} шт
                        {Number(p.defect_qty) > 0 ? ` · брак ${Number(p.defect_qty).toLocaleString('uk-UA')}` : ''}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Вироблено</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{l.produced_qty} шт</Typography>
                    </Box>
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 3, mt: 0.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Відходи</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{l.waste_kg} кг</Typography>
                  </Box>
                  {l.comment && (
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">Коментар</Typography>
                      <Typography variant="body2">{l.comment}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Paper>
          ))}
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 3 }}>
          <Button
            fullWidth variant="contained" size="large"
            onClick={openFreshShiftReport}
            sx={{ bgcolor: '#f57f17', borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#e65100' } }}
          >
            Редагувати та подати повторно
          </Button>
          <Button fullWidth variant="outlined" size="large" onClick={() => setShiftReportAlreadySubmittedOpen(false)}
            sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none' }}>
            Закрити
          </Button>
        </DialogActions>
      </Dialog>

      {/* Shift report */}
      <Dialog open={shiftReportOpen} onClose={() => !shiftReportSubmitting && !shiftReportLoading && setShiftReportOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        {shiftReportDone ? (
          <>
            <Box sx={{ bgcolor: '#2e7d32', color: 'white', px: 3, py: 3, textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 56, mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Звіт подано!</Typography>
            </Box>
            <DialogContent sx={{ textAlign: 'center', pt: 3 }}>
              <Typography color="text.secondary">Зміну завершено успішно.</Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button fullWidth variant="contained" size="large" onClick={() => setShiftReportOpen(false)}
                sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none' }}>Закрити</Button>
            </DialogActions>
          </>
        ) : shiftReportLoading && shiftReportLines.length === 0 ? (
          <>
            <Box sx={{ bgcolor: '#1a237e', color: 'white', px: 3, py: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Звіт за зміну</Typography>
            </Box>
            <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress />
            </DialogContent>
          </>
        ) : shiftReportLines.length === 0 ? (
          <>
            <Box sx={{ bgcolor: '#1a237e', color: 'white', px: 3, py: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Звіт за зміну</Typography>
            </Box>
            <DialogContent>
              <Typography color="text.secondary">Немає прийнятих замовлень в роботі.</Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button fullWidth variant="outlined" onClick={() => setShiftReportOpen(false)} sx={{ textTransform: 'none', borderRadius: 2 }}>Закрити</Button>
            </DialogActions>
          </>
        ) : (() => {
          const stepOrder = shiftReportLines[shiftReportStep]?.order;
          const stepKit = getOrderKitParts(stepOrder);
          return (
          <>
            <Box sx={{ bgcolor: '#1a237e', color: 'white', px: 3, py: 2 }}>
              <Typography variant="overline" sx={{ opacity: 0.8 }}>
                Крок {shiftReportStep + 1} з {shiftReportLines.length}
              </Typography>
              <LinearProgress variant="determinate"
                value={((shiftReportStep + 1) / shiftReportLines.length) * 100}
                sx={{ my: 0.5, bgcolor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { bgcolor: '#a5d6a7' } }} />
              <KitPartsList
                order={stepOrder}
                sx={{
                  mt: 0.5,
                  '& .MuiTypography-root': { color: 'inherit' },
                  '& .MuiTypography-caption': { color: 'rgba(255,255,255,0.85) !important' },
                }}
              />
              <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
                {stepOrder?.machine_identifier}
                {stepKit.isKit
                  ? ` · комплект ${stepKit.parts.length} поз.`
                  : ` · план ${stepOrder?.quantity} шт`}
              </Typography>
            </Box>
            <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {stepKit.isKit ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Вироблено по деталях (шт)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25 }}>
                    Кожна деталь комплекту окремо — через брак кількості можуть відрізнятися
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {stepKit.parts.map((p) => {
                      const line = shiftReportLines[shiftReportStep];
                      const row = line?.kit_part_qtys?.[p.key] || {};
                      return (
                        <TextField
                          key={p.key}
                          label={`${p.name} (план ${Number(p.qty).toLocaleString('uk-UA')})`}
                          type="number"
                          fullWidth
                          size="small"
                          value={row.produced_qty ?? ''}
                          onChange={(e) => {
                            const u = [...shiftReportLines];
                            const cur = { ...(u[shiftReportStep].kit_part_qtys || {}) };
                            cur[p.key] = {
                              ...(cur[p.key] || {
                                product_id: p.product_id,
                                co_id: p.co_id,
                                is_main: p.is_main,
                                name: p.name,
                              }),
                              produced_qty: e.target.value,
                            };
                            u[shiftReportStep] = { ...u[shiftReportStep], kit_part_qtys: cur };
                            if (p.is_main) {
                              u[shiftReportStep].produced_qty = e.target.value;
                            }
                            setShiftReportLines(u);
                          }}
                          inputProps={{ min: 0 }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              ) : (
                <TextField
                  label="Вироблено (шт)"
                  type="number" fullWidth
                  value={shiftReportLines[shiftReportStep]?.produced_qty}
                  onChange={(e) => { const u = [...shiftReportLines]; u[shiftReportStep] = { ...u[shiftReportStep], produced_qty: e.target.value }; setShiftReportLines(u); }}
                  inputProps={{ min: 0 }}
                />
              )}
              <TextField label="Відновлювані відходи (кг)" type="number" fullWidth
                value={shiftReportLines[shiftReportStep]?.waste_kg}
                onChange={(e) => { const u = [...shiftReportLines]; u[shiftReportStep] = { ...u[shiftReportStep], waste_kg: e.target.value }; setShiftReportLines(u); }}
                inputProps={{ min: 0, step: 0.001 }}
                helperText="Підуть на дроблення / переробку" />
              <TextField label="Невідновлювані відходи (кг)" type="number" fullWidth
                value={shiftReportLines[shiftReportStep]?.non_recoverable_waste_kg}
                onChange={(e) => { const u = [...shiftReportLines]; u[shiftReportStep] = { ...u[shiftReportStep], non_recoverable_waste_kg: e.target.value }; setShiftReportLines(u); }}
                inputProps={{ min: 0, step: 0.001 }}
                helperText="Не підлягають переробці" />
              <Box sx={{ bgcolor: '#fff3e0', border: '1px solid #ffb74d', borderRadius: 2, p: 1.5 }}>
                <Typography variant="caption" fontWeight={700} color="#e65100" display="block" mb={1}>
                  Браковані вироби (підуть на дроблення)
                </Typography>
                {stepKit.isKit ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {(() => {
                      const line = shiftReportLines[shiftReportStep];
                      const hasKg = Number(line?.defect_weight_kg) > 0;
                      return (
                        <>
                          <TextField
                            label="Вага браку всього (кг)"
                            type="number"
                            fullWidth
                            size="small"
                            value={line?.defect_weight_kg ?? ''}
                            onChange={(e) => {
                              const u = [...shiftReportLines];
                              const cur = { ...(u[shiftReportStep].kit_part_qtys || {}) };
                              Object.keys(cur).forEach((k) => {
                                cur[k] = { ...cur[k], defect_qty: '' };
                              });
                              u[shiftReportStep] = {
                                ...u[shiftReportStep],
                                kit_part_qtys: cur,
                                defect_qty: '',
                                defect_weight_kg: e.target.value,
                              };
                              setShiftReportLines(u);
                            }}
                            inputProps={{ min: 0, step: 0.001 }}
                            helperText="Якщо не хочете рахувати по кожній деталі — лише загальна вага"
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                            або по деталях (шт):
                          </Typography>
                          {stepKit.parts.map((p) => {
                            const row = line?.kit_part_qtys?.[p.key] || {};
                            return (
                              <TextField
                                key={`def-${p.key}`}
                                label={`Брак: ${p.name} (шт)`}
                                type="number"
                                fullWidth
                                size="small"
                                disabled={hasKg}
                                value={hasKg ? '' : (row.defect_qty ?? '')}
                                onChange={(e) => {
                                  const u = [...shiftReportLines];
                                  const cur = { ...(u[shiftReportStep].kit_part_qtys || {}) };
                                  cur[p.key] = {
                                    ...(cur[p.key] || {
                                      product_id: p.product_id,
                                      co_id: p.co_id,
                                      is_main: p.is_main,
                                      name: p.name,
                                    }),
                                    defect_qty: e.target.value,
                                  };
                                  u[shiftReportStep] = {
                                    ...u[shiftReportStep],
                                    kit_part_qtys: cur,
                                    defect_weight_kg: '',
                                  };
                                  if (p.is_main) {
                                    u[shiftReportStep].defect_qty = e.target.value;
                                  }
                                  setShiftReportLines(u);
                                }}
                                inputProps={{ min: 0 }}
                              />
                            );
                          })}
                          {hasKg ? (
                            <Typography variant="caption" color="#e65100">
                              Загальна вага піде на дроблення; план основної позиції збільшиться за вагою виробу
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="#e65100">
                              Брак по кожній деталі збільшує план відповідної позиції
                            </Typography>
                          )}
                        </>
                      );
                    })()}
                  </Box>
                ) : (
                  <>
                    <Box display="flex" gap={1.5}>
                      <TextField label="Кількість браку (шт)" type="number" fullWidth size="small"
                        value={shiftReportLines[shiftReportStep]?.defect_qty}
                        onChange={(e) => { const u = [...shiftReportLines]; u[shiftReportStep] = { ...u[shiftReportStep], defect_qty: e.target.value, defect_weight_kg: '' }; setShiftReportLines(u); }}
                        inputProps={{ min: 0 }}
                        helperText="або вага нижче" />
                      <TextField label="Вага браку (кг)" type="number" fullWidth size="small"
                        value={shiftReportLines[shiftReportStep]?.defect_weight_kg}
                        onChange={(e) => { const u = [...shiftReportLines]; u[shiftReportStep] = { ...u[shiftReportStep], defect_weight_kg: e.target.value, defect_qty: '' }; setShiftReportLines(u); }}
                        inputProps={{ min: 0, step: 0.001 }}
                        helperText="або кількість вище" />
                    </Box>
                    {(() => {
                      const line = shiftReportLines[shiftReportStep];
                      const dq = Number(line?.defect_qty) || 0;
                      const dw = Number(line?.defect_weight_kg) || 0;
                      const uw = line?.order?.product_weight;
                      if (dq > 0) return (
                        <Typography variant="caption" color="#e65100" mt={0.5} display="block">
                          ⚠ План замовлення збільшиться на {dq} шт
                        </Typography>
                      );
                      if (dw > 0 && uw > 0) {
                        const calc = Math.ceil(dw / uw);
                        return (
                          <Typography variant="caption" color="#e65100" mt={0.5} display="block">
                            ⚠ {dw} кг ÷ {Math.round(Number(uw) * 1000)} г/шт = {calc} шт — план збільшиться на {calc} шт
                          </Typography>
                        );
                      }
                      if (dw > 0 && !uw) return (
                        <Typography variant="caption" color="error" mt={0.5} display="block">
                          ⚠ Вага виробу не вказана у довіднику — вкажіть кількість вручну
                        </Typography>
                      );
                      return null;
                    })()}
                  </>
                )}
              </Box>
              <TextField label="Коментар" fullWidth multiline rows={2}
                value={shiftReportLines[shiftReportStep]?.comment}
                onChange={(e) => { const u = [...shiftReportLines]; u[shiftReportStep] = { ...u[shiftReportStep], comment: e.target.value }; setShiftReportLines(u); }} />
              {shiftReportStep === shiftReportLines.length - 1 && shiftReportMachines.length > 0 && (
                <Box sx={{ mt: 1, p: 1.5, bgcolor: '#fff8e1', borderRadius: 2, border: '1px solid #ffe082' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Показник лічильника ТПА *
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25 }}>
                    Зніміть поточне значення з механічного/електронного лічильника змикань. Без цього звіт не збережеться.
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {shiftReportMachines.map((m) => {
                      const hint = tpaCounterHints[m.id];
                      const prev = hint?.last_counter_reading;
                      return (
                        <TextField
                          key={m.id}
                          label={`Лічильник — ${m.identifier}`}
                          type="number"
                          fullWidth
                          size="small"
                          required
                          value={tpaCounters[m.id] ?? ''}
                          onChange={(e) => {
                            setTpaCounters((prevMap) => ({ ...prevMap, [m.id]: e.target.value }));
                          }}
                          inputProps={{ min: 0, step: 1 }}
                          // Підпис піднімаємо явно, коли значення є — інакше він інколи лягав поверх числа
                          InputLabelProps={String(tpaCounters[m.id] ?? '') !== '' ? { shrink: true } : undefined}
                          helperText={
                            prev != null
                              ? `Попередній кінець зміни: ${prev}`
                              : 'Перший запис для цього ТПА — попереднього показника немає'
                          }
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
              {shiftReportStep === shiftReportLines.length - 1 && (
                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 2, border: '1px solid #a5d6a7' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Ефективність за зміну
                    </Typography>
                    <Button size="small" onClick={refreshEfficiencyPreview} disabled={efficiencyPreviewLoading}>
                      {efficiencyPreviewLoading ? '…' : 'Розрахувати'}
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Норма = 95% номіналу ТПА × відпрацьовані години. Може бути вище 100%.
                  </Typography>
                  {efficiencyPreview && (
                    <>
                      <Typography variant="h6" fontWeight={700} color={efficiencyPreview.requires_low_reason ? 'error.main' : 'success.main'}>
                        {efficiencyPreview.overall_efficiency_pct != null
                          ? `${efficiencyPreview.overall_efficiency_pct}%`
                          : '— (немає номіналу)'}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                        Факт {efficiencyPreview.sum_actual} шт / норма {efficiencyPreview.sum_target} шт
                      </Typography>
                      {efficiencyPreview.requires_low_reason && (
                        <>
                          <Alert severity="warning" sx={{ mb: 1 }}>
                            Ефективність нижче 100% — вкажіть причину (обовʼязково для ЗП).
                          </Alert>
                          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                            <InputLabel>Причина</InputLabel>
                            <Select
                              label="Причина"
                              value={efficiencyLowReason}
                              onChange={(e) => setEfficiencyLowReason(e.target.value)}
                            >
                              {efficiencyReasons.map((r) => (
                                <MenuItem key={r.id} value={r.id}>{r.code} — {r.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            label="Коментар"
                            fullWidth
                            size="small"
                            multiline
                            minRows={2}
                            value={efficiencyLowComment}
                            onChange={(e) => setEfficiencyLowComment(e.target.value)}
                          />
                        </>
                      )}
                    </>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
              {shiftReportStep > 0 && (
                <Button onClick={() => setShiftReportStep((s) => s - 1)} sx={{ textTransform: 'none' }}>← Назад</Button>
              )}
              {shiftReportStep < shiftReportLines.length - 1 ? (
                <Button fullWidth variant="contained" size="large"
                  onClick={() => setShiftReportStep((s) => s + 1)}
                  sx={{ bgcolor: '#1a237e', borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none' }}>
                  Далі →
                </Button>
              ) : (
                <Button fullWidth variant="contained" size="large"
                  startIcon={shiftReportSubmitting ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <AssessmentIcon />}
                  disabled={shiftReportSubmitting} onClick={handleShiftReportSubmit}
                  sx={{ bgcolor: '#2e7d32', borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none' }}>
                  Подати звіт
                </Button>
              )}
            </DialogActions>
          </>
          );
        })()}
      </Dialog>

      {/* Process declaration dialog */}
      <Dialog open={processDeclarationOpen} onClose={() => setProcessDeclarationOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box sx={{ bgcolor: '#1565c0', px: 3, py: 2 }}>
          <Typography variant="h6" fontWeight={800} color="#fff">Додаткові процеси</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.25 }}>
            Визначте участь у кожному процесі перед запуском
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 2 }}>
          {(processDeclarationOrder?.additional_processes || [])
            .filter(p => p.operator_status === 'pending')
            .map(p => (
              <Box key={p.id} sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Typography fontWeight="bold" sx={{ mb: 1 }}>
                  {p.process_icon} {p.process_name}
                </Typography>
                <RadioGroup
                  row
                  value={processDeclarations[p.id] || ''}
                  onChange={e => setProcessDeclarations(d => ({ ...d, [p.id]: e.target.value }))}
                >
                  <FormControlLabel value="self" control={<Radio />} label="Виконаю сам" />
                  <FormControlLabel value="partial" control={<Radio />} label="Частково" />
                  <FormControlLabel value="not" control={<Radio />} label="Не виконаю" />
                </RadioGroup>
              </Box>
            ))
          }
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setProcessDeclarationOpen(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, flex: 1 }}>
            Скасувати
          </Button>
          <Button
            variant="contained"
            disabled={
              (processDeclarationOrder?.additional_processes || [])
                .filter(p => p.operator_status === 'pending')
                .some(p => !processDeclarations[p.id])
            }
            onClick={async () => {
              const order = processDeclarationOrder;
              const performerName = userForOp?.full_name || currentUser?.full_name || '';
              setProcessDeclarationOpen(false);
              setBusyOrderId(order.id);
              try {
                const pendingProcs = (order.additional_processes || []).filter(p => p.operator_status === 'pending');
                await Promise.all(pendingProcs.map(p =>
                  orderAdditionalProcessService.setOperatorStatus(p.id, { operator_status: processDeclarations[p.id] })
                ));
                await productionOrderService.launch(order.id, { performed_by: performerName });
                loadOrders();
              } catch (e) {
                const data = e.response?.data;
                if (data?.can_force) {
                  setPackagingWarn({
                    order,
                    performerName,
                    stage: 'launch',
                    detail: data.detail,
                    items: data.packaging || [],
                  });
                } else {
                  alert(data?.detail || e.message || 'Помилка');
                }
              }
              setBusyOrderId(null);
            }}
            sx={{ bgcolor: '#1565c0', borderRadius: 2, textTransform: 'none', fontWeight: 700, flex: 1 }}
          >
            Продовжити
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Maintenance 5-min popup ── */}
      <Dialog
        open={Boolean(maintPopup)}
        onClose={() => setMaintPopup(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, border: '2px solid #6a1b9a' } }}
      >
        <Box sx={{ bgcolor: '#6a1b9a', color: '#fff', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WarningAmberIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight={700}>Обслуговування через 5 хвилин!</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>Підготуйтесь до зупинки</Typography>
          </Box>
        </Box>
        <DialogContent sx={{ pt: 2 }}>
          {maintPopup && (
            <>
              <Typography variant="body1" fontWeight={700} mb={1}>
                🔧 Необхідно зупинити машину для обслуговування пресформи
              </Typography>
              {maintPopup.machine_name && (
                <Typography variant="body2" mb={0.5}>
                  <b>ТПА:</b> {maintPopup.machine_name}
                  {maintPopup.machine_identifier ? ` (${maintPopup.machine_identifier})` : ''}
                </Typography>
              )}
              {maintPopup.press_form_identifier && (
                <Typography variant="body2" mb={0.5}>
                  <b>Прес-форма:</b> {maintPopup.press_form_identifier}
                  {maintPopup.press_form_name ? ` — ${maintPopup.press_form_name}` : ''}
                </Typography>
              )}
              {maintPopup.scheduled_at && (
                <Typography variant="body2" mb={0.5} color="#6a1b9a" fontWeight={700}>
                  <b>Зупинити о:</b> {fmtTime(maintPopup.scheduled_at)}
                </Typography>
              )}
              <Typography variant="body2" mt={1} color="text.secondary">
                {maintPopup.reason}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            fullWidth
            variant="contained"
            sx={{ bgcolor: '#6a1b9a', '&:hover': { bgcolor: '#4a148c' }, borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
            onClick={() => setMaintPopup(null)}
          >
            Зрозуміло
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Діалог "Взяти зі складу" ───────────────────── */}
      <Dialog open={transferOpen} onClose={() => !transferSubmitting && setTransferOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MoveToInboxIcon sx={{ color: '#7b1fa2' }} />
            Взяти матеріал зі складу
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Матеріал буде списано з основного складу та зараховано на склад виробництва
          </Typography>
          <Autocomplete
            options={transferStockItems}
            getOptionLabel={(o) => {
              const name = o.raw_material_name || o.dye_name || o.packaging_material_name || o.consumable_material_name || o.product_name || `#${o.id}`;
              return `${name} (${o.quantity} ${o.unit})`;
            }}
            groupBy={(o) => {
              if (o.raw_material_name) return 'Сировина';
              if (o.dye_name) return 'Барвники';
              if (o.packaging_material_name) return 'Упаковка';
              if (o.consumable_material_name) return 'Розхідники';
              if (o.product_name) return 'Вироби';
              return 'Інше';
            }}
            value={transferSelected}
            onChange={(_, v) => { setTransferSelected(v); setTransferQty(''); setTransferError(''); }}
            renderInput={(params) => (
              <TextField {...params} label="Матеріал" placeholder="Пошук за назвою..." size="small" sx={{ mb: 2 }} />
            )}
            noOptionsText={transferStockItems.length === 0 ? 'Завантаження...' : 'Нічого не знайдено'}
            filterOptions={(opts, state) => {
              const q = state.inputValue.toLowerCase();
              return q ? opts.filter(o => {
                const name = (o.raw_material_name || o.dye_name || o.packaging_material_name || o.consumable_material_name || o.product_name || '').toLowerCase();
                return name.includes(q);
              }) : opts;
            }}
          />
          {transferSelected && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Доступно на складі: <b>{transferSelected.quantity} {transferSelected.unit}</b>
            </Typography>
          )}
          <TextField
            label="Кількість"
            type="number"
            size="small"
            fullWidth
            value={transferQty}
            onChange={(e) => { setTransferQty(e.target.value); setTransferError(''); }}
            inputProps={{ min: 0.001, step: 'any' }}
            sx={{ mb: 2 }}
            disabled={!transferSelected}
          />
          <TextField
            label="Примітка (необов'язково)"
            size="small"
            fullWidth
            value={transferNote}
            onChange={(e) => setTransferNote(e.target.value)}
            multiline
            rows={2}
          />
          {transferError && (
            <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
              {transferError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setTransferOpen(false)} disabled={transferSubmitting} sx={{ textTransform: 'none', borderRadius: 2 }}>
            Скасувати
          </Button>
          <Button
            variant="contained"
            onClick={handleTransferSubmit}
            disabled={transferSubmitting || !transferSelected || !transferQty}
            sx={{ bgcolor: '#7b1fa2', '&:hover': { bgcolor: '#6a1b9a' }, textTransform: 'none', fontWeight: 700, borderRadius: 2, minWidth: 140 }}
          >
            {transferSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Передати на виробництво'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Shift Handover Dialog ──────────────────────── */}
      <Dialog open={handoverDialogOpen} onClose={() => !handoverSubmitting && setHandoverDialogOpen(false)}
        fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SwapHorizIcon sx={{ color: '#0288d1' }} />
            <Typography fontWeight={700} fontSize="1.05rem">Передача зміни</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {!lastSubmittedReport ? (
            <Box sx={{ p: 1 }}>
              <Typography variant="body1" fontWeight={600} color="warning.main" mb={1}>
                ⚠️ Звіт за зміну не подано
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Перед передачею зміни необхідно подати звіт про виконану роботу. Після виходу наступний оператор зможе увійти та побачить свої ТПА.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ p: 1 }}>
              <Typography variant="body1" fontWeight={600} color="success.main" mb={1}>
                ✅ Звіт за зміну подано
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Після підтвердження вас буде виведено із системи. Наступний оператор зможе увійти та побачить свої призначені ТПА.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, flexDirection: 'column' }}>
          {!lastSubmittedReport && (
            <Button fullWidth variant="contained" size="large"
              onClick={() => { setHandoverDialogOpen(false); handleOpenShiftReport(); }}
              sx={{ bgcolor: '#f57c00', '&:hover': { bgcolor: '#e65100' }, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
              Подати звіт за зміну
            </Button>
          )}
          <Button fullWidth variant="contained" size="large"
            onClick={handleHandoverSubmit}
            disabled={handoverSubmitting}
            sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' }, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
            {handoverSubmitting
              ? <CircularProgress size={20} color="inherit" />
              : lastSubmittedReport ? 'Передати зміну та вийти' : 'Все одно вийти'}
          </Button>
          <Button fullWidth variant="outlined" size="large"
            onClick={() => setHandoverDialogOpen(false)}
            disabled={handoverSubmitting}
            sx={{ textTransform: 'none', borderRadius: 2 }}>
            Скасувати
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
